import { useState, useEffect, useRef } from "react";
import api from "./api";

// ═══════════════════════════════════════════════════════════════
// SUGAR CHECK REMINDER — Web Notifications + Vibration API
// ═══════════════════════════════════════════════════════════════
function useCheckReminder(logs) {
  const permissionRef  = useRef("default");
  const swRef          = useRef(null);
  const lastAlertRef   = useRef(null);
  const INTERVAL       = 2 * 60 * 60 * 1000; // 2 hours
  const SNOOZE         = 15 * 60 * 1000;      // 15 min snooze

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js")
        .then(r => { swRef.current = r; })
        .catch(() => {});
    }
    if ("Notification" in window) {
      permissionRef.current = Notification.permission;
    }
  }, []);

  async function requestPermission() {
    if (!("Notification" in window)) return false;
    if (Notification.permission === "granted") { permissionRef.current = "granted"; return true; }
    if (Notification.permission === "denied")  return false;
    const result = await Notification.requestPermission();
    permissionRef.current = result;
    return result === "granted";
  }

  function vibrate(pattern = [200, 100, 200, 100, 400]) {
    if ("vibrate" in navigator) navigator.vibrate(pattern);
  }

  function showNotification(title, body) {
    vibrate();
    if (swRef.current?.active) {
      swRef.current.active.postMessage({ type: "SUGAR_REMINDER", message: body });
      return;
    }
    if (Notification.permission === "granted") {
      new Notification(title, { body, icon: "/favicon.ico", tag: "choptime-reminder", requireInteraction: true });
    }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      if (permissionRef.current !== "granted") return;
      const lastLog = logs[0];
      if (!lastLog) return;
      const lastTime = new Date(lastLog.logged_at || lastLog.created_at);
      const now      = new Date();
      if (lastAlertRef.current && (now - lastAlertRef.current) < SNOOZE) return;
      if ((now - lastTime) > INTERVAL) {
        const h = Math.floor((now - lastTime) / 3600000);
        showNotification(
          "⏰ ChopTime AI — Sugar Check Due",
          `You last logged ${h}h ago. Time to check your blood sugar and stay safe.`
        );
        lastAlertRef.current = now;
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [logs]);

  return { requestPermission, vibrate, showNotification };
}


/*
 ╔══════════════════════════════════════════════════════════╗
 ║  CHOPTIME AI — Dashboard.js                             ║
 ║  Full app — 8 screens                                   ║
 ║  Desktop: sidebar nav                                   ║
 ║  Mobile: bottom tab nav                                 ║
 ║  Dark / Light mode toggle                               ║
 ║  West Africa: GH · NG · SN · CI · CM                   ║
 ╚══════════════════════════════════════════════════════════╝
*/

/* ─── STYLES ──────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

/* ── DARK THEME (default) ── */
:root{
  --bg0:#06090b; --bg1:#0c1419; --bg2:#111d24;
  --bg3:#192830; --bg4:#1f3040;
  --teal:#00d4a0; --teal2:#009e78; --teal3:#00f5b8;
  --teal-dim:rgba(0,212,160,.1); --teal-glow:rgba(0,212,160,.22);
  --amber:#ffab2e; --amber-dim:rgba(255,171,46,.1);
  --red:#ff5252; --red-dim:rgba(255,82,82,.1);
  --blue:#4db8ff; --blue-dim:rgba(77,184,255,.1);
  --text:#deeee8; --text2:#6e9e8c; --text3:#2e4e44;
  --border:rgba(255,255,255,.055); --border2:rgba(0,212,160,.22);
  --r-sm:10px; --r-md:16px; --r-lg:22px; --r-xl:30px;
  --glow:0 0 32px rgba(0,212,160,.18);
  --shadow:0 8px 48px rgba(0,0,0,.6);
}

/* ── LIGHT THEME ── */
:root.light{
  --bg0:#f0f7f4; --bg1:#ffffff; --bg2:#f5faf8;
  --bg3:#e8f4ef; --bg4:#d4ebe2;
  --teal:#008f6b; --teal2:#006b50; --teal3:#00b388;
  --teal-dim:rgba(0,143,107,.1); --teal-glow:rgba(0,143,107,.2);
  --amber:#c47c00; --amber-dim:rgba(196,124,0,.1);
  --red:#d93025; --red-dim:rgba(217,48,37,.08);
  --blue:#1a6fc4; --blue-dim:rgba(26,111,196,.1);
  --text:#0d2a1f; --text2:#3d6e5a; --text3:#8ab8a4;
  --border:rgba(0,0,0,.08); --border2:rgba(0,143,107,.3);
  --glow:0 0 24px rgba(0,143,107,.15);
  --shadow:0 4px 24px rgba(0,0,0,.1);
}

html,body{height:100%;background:var(--bg0);overflow:hidden;transition:background .3s}
body{font-family:'DM Sans',sans-serif;color:var(--text);-webkit-font-smoothing:antialiased}
::-webkit-scrollbar{width:3px}
::-webkit-scrollbar-thumb{background:var(--bg4);border-radius:4px}

/* smooth theme transition */
.d-shell,.d-sidebar,.d-main,.d-card,.d-card-teal,.d-card-amber,
.d-sc,.d-voice-box,.d-quiz-wrap,.d-post,.d-ep-card,.d-joint-card,
.d-log-row,.d-map-panel,.d-bottom-nav,.d-mobile-topbar{
  transition:background .3s,border-color .3s,color .2s
}

/* ── ANIMATIONS ── */
@keyframes d-fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes d-spin{to{transform:rotate(360deg)}}
@keyframes d-blink{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes d-pulse{0%,100%{box-shadow:0 0 0 0 rgba(0,212,160,.4)}50%{box-shadow:0 0 0 14px rgba(0,212,160,0)}}
@keyframes d-wv{0%,100%{height:3px}50%{height:15px}}
@keyframes d-slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}@keyframes d-fadeIn{from{opacity:0}to{opacity:1}}
@keyframes d-heartbeat{0%,100%{transform:scale(1)}14%{transform:scale(1.18)}42%{transform:scale(1.1)}70%{transform:scale(1)}}

.d-page{display:none;padding:26px 26px 80px;animation:d-fadeUp .22s ease}
.d-page.active{display:block}

/* ── SHELL ── */
.d-shell{display:flex;height:100vh;width:100%;overflow:hidden;position:relative}
.d-shell::before{content:'';position:fixed;inset:0;
  background:radial-gradient(ellipse 55% 35% at 15% 5%,rgba(0,212,160,.07) 0%,transparent 55%),
             radial-gradient(ellipse 40% 25% at 85% 85%,rgba(255,171,46,.04) 0%,transparent 55%);
  pointer-events:none;z-index:0}

/* ── SIDEBAR (desktop) ── */
.d-sidebar{width:230px;min-width:230px;background:var(--bg1);border-right:1px solid var(--border);
  display:flex;flex-direction:column;position:relative;z-index:10;flex-shrink:0}
.d-logo-wrap{padding:22px 20px 18px;border-bottom:1px solid var(--border)}
.d-logo-row{display:flex;align-items:center;gap:11px}
.d-logo-icon{width:38px;height:38px;border-radius:11px;
  background:linear-gradient(135deg,var(--teal),var(--teal2));
  display:flex;align-items:center;justify-content:center;font-size:20px;
  box-shadow:0 0 20px var(--teal-glow);flex-shrink:0}
.d-logo-name{font-family:'Syne',sans-serif;font-weight:900;font-size:16px;letter-spacing:-.3px}
.d-logo-name em{color:var(--teal);font-style:normal}
.d-logo-tag{font-size:10px;color:var(--text3);margin-top:2px;letter-spacing:.3px}
.d-country-sel{padding:12px 16px;border-bottom:1px solid var(--border)}
.d-country-lbl{font-size:9px;color:var(--text3);text-transform:uppercase;letter-spacing:.8px;margin-bottom:5px;font-weight:600}
.d-country-drop{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:var(--r-sm);
  padding:7px 10px;font-family:'DM Sans',sans-serif;font-size:13px;color:var(--text);cursor:pointer;outline:none;transition:border-color .15s}
.d-country-drop:focus{border-color:var(--teal)}
option{background:var(--bg2)}
.d-nav-wrap{flex:1;padding:10px}
.d-nav-lbl{font-size:9px;color:var(--text3);text-transform:uppercase;letter-spacing:1px;font-weight:600;padding:10px 10px 5px}
.d-nav-item{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:var(--r-sm);
  cursor:pointer;transition:all .15s;margin-bottom:2px;border:1px solid transparent}
.d-nav-item:hover{background:var(--bg3)}
.d-nav-item.active{background:var(--teal-dim);border-color:var(--border2)}
.d-nav-item.active .d-ni-icon svg{stroke:var(--teal)}
.d-nav-item.active .d-ni-label{color:var(--teal)}
.d-ni-icon{width:30px;height:30px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.d-ni-icon svg{width:16px;height:16px;stroke:var(--text2);fill:none;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round;transition:.15s}
.d-ni-label{font-size:13px;font-weight:500;color:var(--text2);transition:.15s;flex:1}
.d-ni-badge{background:var(--teal);color:var(--bg0);font-size:9px;font-weight:700;padding:2px 7px;border-radius:20px}
.d-sidebar-foot{padding:0 0 0;border-top:1px solid var(--border)}
.d-theme-toggle{display:flex;align-items:center;gap:9px;padding:12px 16px;cursor:pointer;user-select:none;border-bottom:1px solid var(--border)}
.d-toggle-lbl{font-size:11px;font-weight:600;color:var(--text2)}
.d-toggle-track{width:38px;height:21px;border-radius:11px;background:var(--bg4);border:1px solid var(--border);position:relative;cursor:pointer;transition:background .3s,border-color .3s;flex-shrink:0;margin-left:auto}
.d-toggle-track.on{background:var(--teal);border-color:var(--teal)}
.d-toggle-thumb{position:absolute;top:2px;left:2px;width:15px;height:15px;border-radius:50%;background:#fff;transition:transform .25s cubic-bezier(.34,1.56,.64,1);box-shadow:0 1px 4px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-size:9px}
.d-toggle-track.on .d-toggle-thumb{transform:translateX(17px)}
.d-user-row{display:flex;align-items:center;gap:10px;padding:14px 16px}
.d-user-av{width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,var(--teal),var(--teal2));display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:900;font-size:13px;color:var(--bg0);flex-shrink:0}
.d-user-name{font-size:12px;font-weight:600;color:var(--text)}
.d-user-plan{font-size:10px;color:var(--teal);font-weight:500}

/* ── MAIN ── */
.d-main{flex:1;overflow-y:auto;overflow-x:hidden;position:relative;z-index:1}

/* ── TOPBAR ── */
.d-topbar{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:22px}
.d-page-title{font-family:'Syne',sans-serif;font-size:22px;font-weight:900;letter-spacing:-.3px}
.d-page-sub{font-size:12px;color:var(--text2);margin-top:3px}
.d-topbar-right{display:flex;align-items:center;gap:9px;flex-shrink:0}

/* ── BUTTONS ── */
.d-btn{display:inline-flex;align-items:center;gap:6px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;padding:8px 16px;border-radius:var(--r-sm);cursor:pointer;border:none;transition:all .15s;white-space:nowrap}
.d-btn-teal{background:var(--teal);color:var(--bg0)}
.d-btn-teal:hover{background:var(--teal3);transform:translateY(-1px);box-shadow:0 4px 18px var(--teal-glow)}
.d-btn-ghost{background:var(--bg3);color:var(--text2);border:1px solid var(--border)}
.d-btn-ghost:hover{color:var(--text);border-color:var(--border2)}

/* ── CARDS ── */
.d-card{background:var(--bg1);border:1px solid var(--border);border-radius:var(--r-md);padding:20px;position:relative;overflow:hidden}
.d-card-teal{background:linear-gradient(140deg,#071d15,#0b2a1e);border:1px solid rgba(0,212,160,.22);border-radius:var(--r-md);padding:20px;box-shadow:var(--glow)}
.d-card-amber{background:linear-gradient(140deg,#170e00,#1f1500);border:1px solid rgba(255,171,46,.2);border-radius:var(--r-md);padding:20px}
.d-card-blue{background:linear-gradient(140deg,#071020,#0b1a30);border:1px solid rgba(77,184,255,.2);border-radius:var(--r-md);padding:20px}
.d-card-red{background:linear-gradient(140deg,#170808,#1f0c0c);border:1px solid rgba(255,82,82,.2);border-radius:var(--r-md);padding:20px}

/* light mode card overrides */
:root.light .d-card-teal{background:linear-gradient(140deg,#e0f5ec,#ccf0e2);border-color:rgba(0,143,107,.25)}
:root.light .d-card-amber{background:linear-gradient(140deg,#fff8e8,#fef0cc);border-color:rgba(196,124,0,.25)}
:root.light .d-card-blue{background:linear-gradient(140deg,#e8f2ff,#d4e8ff);border-color:rgba(26,111,196,.2)}
:root.light .d-card-red{background:linear-gradient(140deg,#fdecea,#fbd8d6);border-color:rgba(217,48,37,.2)}
:root.light .d-voice-box{background:linear-gradient(140deg,#d8f5ec,#e8faf3);border-color:rgba(0,143,107,.3)}
:root.light .d-quiz-wrap{background:linear-gradient(145deg,#e0f5ec,#ccf0e2)}
:root.light .d-map-header{background:linear-gradient(90deg,#d4f0e4,#e0f7ec)}

/* ── GRIDS ── */
.d-g4{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px}
.d-g3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:18px}
.d-g2{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:18px}
.d-g21{display:grid;grid-template-columns:2fr 1fr;gap:16px;margin-bottom:18px}
.d-g12{display:grid;grid-template-columns:1fr 2fr;gap:16px;margin-bottom:18px}

/* ── STAT CARD ── */
.d-sc{background:var(--bg1);border:1px solid var(--border);border-radius:var(--r-md);padding:16px 18px;overflow:hidden;position:relative;transition:border-color .2s;cursor:default}
.d-sc:hover{border-color:var(--border2)}
.d-sc::after{content:'';position:absolute;top:-20px;right:-20px;width:80px;height:80px;border-radius:50%}
.d-sc.t::after{background:radial-gradient(var(--teal-glow),transparent 70%)}
.d-sc.a::after{background:radial-gradient(rgba(255,171,46,.15),transparent 70%)}
.d-sc.r::after{background:radial-gradient(rgba(255,82,82,.12),transparent 70%)}
.d-sc.b::after{background:radial-gradient(rgba(77,184,255,.12),transparent 70%)}
.d-sc-lbl{font-size:10px;font-weight:600;color:var(--text2);letter-spacing:.5px;text-transform:uppercase;margin-bottom:7px}
.d-sc-val{font-family:'Syne',sans-serif;font-size:24px;font-weight:900;line-height:1;margin-bottom:5px}
.d-sc-val.t{color:var(--teal)}.d-sc-val.a{color:var(--amber)}.d-sc-val.r{color:var(--red)}
.d-sc-delta{font-size:11px;font-weight:600}
.d-sc-delta.up{color:var(--teal)}.d-sc-delta.dn{color:var(--red)}

/* ── SECTION HEADER ── */
.d-sh{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.d-sh-title{font-family:'Syne',sans-serif;font-size:14px;font-weight:800;letter-spacing:-.1px}
.d-sh-link{font-size:12px;color:var(--teal);cursor:pointer;font-weight:500}

/* ── CHIPS ── */
.d-chip{display:inline-flex;align-items:center;font-size:10px;font-weight:700;padding:3px 9px;border-radius:20px;letter-spacing:.2px}
.d-chip-teal{background:rgba(0,212,160,.13);color:var(--teal);border:1px solid rgba(0,212,160,.28)}
.d-chip-amber{background:rgba(255,171,46,.13);color:var(--amber);border:1px solid rgba(255,171,46,.28)}
.d-chip-red{background:rgba(255,82,82,.13);color:var(--red);border:1px solid rgba(255,82,82,.28)}
.d-chip-gray{background:var(--bg3);color:var(--text2);border:1px solid var(--border)}

/* ── VOICE BOX ── */
.d-voice-box{background:linear-gradient(140deg,#04120d,#071a13,#04120d);border:1px solid rgba(0,212,160,.28);border-radius:var(--r-lg);padding:22px;position:relative;overflow:hidden;margin-bottom:18px;box-shadow:0 0 60px rgba(0,212,160,.07)}
.d-voice-box::before{content:'';position:absolute;top:-60px;right:-60px;width:220px;height:220px;border-radius:50%;background:radial-gradient(circle,rgba(0,212,160,.1),transparent 70%);pointer-events:none}
.d-voice-hdr{display:flex;align-items:center;gap:14px;margin-bottom:14px}
.d-mic-btn{width:50px;height:50px;border-radius:50%;background:rgba(0,212,160,.1);border:2px solid rgba(0,212,160,.35);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;flex-shrink:0}
.d-mic-btn:hover{background:rgba(0,212,160,.2);transform:scale(1.05)}
.d-mic-btn.on{animation:d-pulse 1.4s ease-in-out infinite}
.d-mic-btn svg{width:22px;height:22px;stroke:var(--teal);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.d-voice-title{font-family:'Syne',sans-serif;font-size:16px;font-weight:800}
.d-voice-sub{font-size:12px;color:var(--text2);margin-top:2px}
.d-vchips{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:12px}
.d-vchip{background:rgba(0,212,160,.08);border:1px solid rgba(0,212,160,.22);border-radius:20px;font-size:11px;font-weight:600;color:var(--teal);padding:5px 12px;cursor:pointer;transition:all .15s}
.d-vchip:hover{background:rgba(0,212,160,.18);transform:translateY(-1px)}
.d-ai-box{background:rgba(0,212,160,.06);border:1px solid rgba(0,212,160,.15);border-left:3px solid var(--teal);border-radius:0 12px 12px 0;padding:14px 16px;font-size:13px;line-height:1.75;display:none;animation:d-fadeUp .3s ease}
.d-ai-box.show{display:block}
.d-ai-box strong{color:var(--teal3)}
.d-wave{display:flex;align-items:center;gap:2.5px;height:18px;margin-left:auto}
.d-wave span{display:block;width:3px;background:var(--teal);border-radius:2px;animation:d-wv 1s ease-in-out infinite}
.d-wave span:nth-child(1){animation-delay:0s}.d-wave span:nth-child(2){animation-delay:.1s}
.d-wave span:nth-child(3){animation-delay:.2s}.d-wave span:nth-child(4){animation-delay:.3s}
.d-wave span:nth-child(5){animation-delay:.2s}

/* ── RING ── */
.d-ring-wrap{position:relative;width:130px;height:130px;flex-shrink:0}
.d-ring-wrap svg{width:130px;height:130px;transform:rotate(-90deg)}
.d-ring-center{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center}
.d-ring-val{font-family:'Syne',sans-serif;font-size:28px;font-weight:900;line-height:1}
.d-ring-unit{font-size:10px;color:var(--text2);margin-top:1px}
.d-ring-status{font-size:11px;font-weight:700;color:var(--teal);margin-top:4px}

/* ── BARS ── */
.d-bars{display:flex;align-items:flex-end;gap:4px;height:70px}
.d-bar{flex:1;border-radius:4px 4px 0 0;min-height:3px;transition:height .5s cubic-bezier(.34,1.56,.64,1);cursor:pointer}
.d-bar:hover{filter:brightness(1.25)}
.d-bt{background:linear-gradient(to top,var(--teal2),var(--teal))}
.d-ba{background:linear-gradient(to top,#b87800,var(--amber))}
.d-br{background:linear-gradient(to top,#c02020,var(--red))}
.d-chart-lbl{display:flex;justify-content:space-between;font-size:9px;color:var(--text3);margin-top:5px}

/* ── LOG ROWS ── */
.d-log-row{display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:var(--r-sm);margin-bottom:5px;background:var(--bg2);border:1px solid var(--border);transition:border-color .15s}
.d-log-row:hover{border-color:var(--border2)}
.d-log-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0}
.d-log-time{font-size:11px;color:var(--text2);width:58px}
.d-log-val{font-family:'Syne',sans-serif;font-size:16px;font-weight:800;flex:1}
.d-log-meal{font-size:11px;color:var(--text2)}
.d-log-tag{font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px}

/* ── FOOD ROWS ── */
.d-food-row{display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--border)}
.d-food-row:last-child{border:none;padding-bottom:0}
.d-food-em{font-size:20px;width:38px;height:38px;background:var(--bg3);border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.d-food-nm{font-size:13px;font-weight:600;margin-bottom:1px}
.d-food-why{font-size:11px;color:var(--text2);line-height:1.4}
.d-food-right{text-align:right;flex-shrink:0}
.d-food-port{font-size:10px;color:var(--text3);margin-top:3px}

/* ── MEAL TABS ── */
.d-mtabs{display:flex;gap:5px;background:var(--bg2);padding:4px;border-radius:var(--r-sm);border:1px solid var(--border);margin-bottom:16px}
.d-mtab{flex:1;text-align:center;padding:7px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;color:var(--text2);transition:all .15s}
.d-mtab.active{background:var(--teal);color:var(--bg0)}

/* ── NEARBY MAP ── */
.d-map-panel{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-lg);overflow:hidden;margin-bottom:18px}
.d-map-header{background:linear-gradient(90deg,#071a13,#0b2a1e);padding:14px 18px;border-bottom:1px solid rgba(0,212,160,.15);display:flex;align-items:center;justify-content:space-between}
.d-map-title{font-family:'Syne',sans-serif;font-size:14px;font-weight:800}
.d-map-live{display:flex;align-items:center;gap:6px;font-size:11px;color:var(--teal);font-weight:600}
.d-map-live-dot{width:7px;height:7px;border-radius:50%;background:var(--teal);animation:d-blink 1.5s ease-in-out infinite}
.d-map-body{padding:16px 18px}
.d-city-tabs{display:flex;gap:5px;margin-bottom:14px;flex-wrap:wrap}
.d-city-tab{padding:5px 12px;border-radius:20px;font-size:11px;font-weight:600;cursor:pointer;transition:all .15s;background:var(--bg3);color:var(--text2);border:1px solid var(--border)}
.d-city-tab.active{background:var(--teal);color:var(--bg0);border-color:var(--teal)}
.d-joint-card{background:var(--bg1);border:1px solid var(--border);border-radius:var(--r-sm);padding:12px 14px;display:flex;align-items:center;gap:12px;transition:all .15s;cursor:pointer;margin-bottom:8px;position:relative}
.d-joint-card:hover{border-color:var(--border2);transform:translateX(2px)}
.d-joint-card.best{border-color:rgba(0,212,160,.35);background:rgba(0,212,160,.04)}
.d-joint-emoji{font-size:22px;width:44px;height:44px;border-radius:10px;background:var(--bg3);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.d-joint-name{font-size:13px;font-weight:700;margin-bottom:2px}
.d-joint-food{font-size:11px;color:var(--text2);margin-bottom:5px}
.d-joint-meta{display:flex;gap:8px;flex-wrap:wrap}
.d-joint-dist{font-size:11px;font-weight:600;color:var(--teal)}
.d-joint-time{font-size:11px;color:var(--text2)}
.d-traffic-pill{font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px}
.d-traffic-clear{background:rgba(0,212,160,.12);color:var(--teal)}
.d-traffic-mod{background:rgba(255,171,46,.12);color:var(--amber)}
.d-traffic-heavy{background:rgba(255,82,82,.12);color:var(--red)}
.d-best-badge{position:absolute;top:-1px;right:8px;background:var(--teal);color:var(--bg0);font-size:9px;font-weight:700;padding:2px 8px;border-radius:0 0 8px 8px}

/* ── EPISODE CARD ── */
.d-ep-card{display:flex;align-items:center;gap:12px;padding:13px;border-radius:var(--r-md);background:var(--bg1);border:1px solid var(--border);margin-bottom:8px;cursor:pointer;transition:all .15s}
.d-ep-card:hover{border-color:var(--border2);transform:translateX(3px)}
.d-ep-thumb{width:52px;height:52px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0;background:var(--bg3)}
.d-ep-num{font-size:10px;color:var(--teal);font-weight:700;letter-spacing:.5px;text-transform:uppercase;margin-bottom:3px}
.d-ep-title{font-size:13px;font-weight:600;line-height:1.35;margin-bottom:4px}
.d-ep-meta{font-size:11px;color:var(--text2)}
.d-play-btn{width:34px;height:34px;border-radius:50%;background:rgba(0,212,160,.12);border:1px solid rgba(0,212,160,.3);display:flex;align-items:center;justify-content:center;flex-shrink:0;cursor:pointer;transition:all .15s}
.d-play-btn:hover{background:var(--teal);box-shadow:0 0 16px var(--teal-glow)}
.d-play-btn:hover svg{fill:var(--bg0)}
.d-play-btn svg{width:11px;height:13px;fill:var(--teal);stroke:none}

/* ── POST ── */
.d-post{background:var(--bg1);border:1px solid var(--border);border-radius:var(--r-md);padding:15px;margin-bottom:9px;transition:border-color .15s}
.d-post:hover{border-color:var(--border2)}
.d-post-av{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:900;font-size:13px;flex-shrink:0}
.d-post-body{font-size:13px;line-height:1.65;color:var(--text2);margin:9px 0}
.d-post-act{font-size:12px;color:var(--text3);cursor:pointer;transition:color .15s}
.d-post-act:hover{color:var(--teal)}

/* ── QUIZ ── */
.d-quiz-wrap{background:linear-gradient(145deg,#04120d,#071a13,#040e09);border:1px solid rgba(0,212,160,.22);border-radius:var(--r-xl);padding:26px;box-shadow:0 0 60px rgba(0,212,160,.07);margin-bottom:18px}
.d-quiz-q{font-family:'Syne',sans-serif;font-size:18px;font-weight:800;margin:18px 0 20px;line-height:1.4}
.d-quiz-opts{display:grid;grid-template-columns:1fr 1fr;gap:9px}
.d-qopt{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.09);border-radius:var(--r-sm);padding:13px 15px;font-size:13px;font-weight:500;color:var(--text);cursor:pointer;transition:all .15s;line-height:1.4}
.d-qopt:hover{background:rgba(0,212,160,.1);border-color:rgba(0,212,160,.3)}
.d-qopt.correct{background:rgba(0,212,160,.18);border-color:var(--teal);color:var(--teal)}
.d-qopt.wrong{background:rgba(255,82,82,.14);border-color:var(--red);color:var(--red)}
.d-qopt.off{pointer-events:none}

/* ── PROGRESS ── */
.d-prog{height:5px;background:var(--bg4);border-radius:3px;overflow:hidden;margin:7px 0}
.d-prog-fill{height:100%;border-radius:3px;background:linear-gradient(90deg,var(--teal2),var(--teal3));transition:width .7s cubic-bezier(.34,1.56,.64,1)}

/* ── ALERTS ── */
.d-alert{display:flex;align-items:flex-start;gap:11px;padding:13px 15px;border-radius:var(--r-md);margin-bottom:9px;border:1px solid}
.d-alert.warn{background:var(--amber-dim);border-color:rgba(255,171,46,.25)}
.d-alert.ok{background:var(--teal-dim);border-color:rgba(0,212,160,.25)}
.d-alert.bad{background:var(--red-dim);border-color:rgba(255,82,82,.25)}
.d-alert-icon{font-size:20px;flex-shrink:0;margin-top:1px}
.d-alert-title{font-size:13px;font-weight:700;margin-bottom:3px}
.d-alert.warn .d-alert-title{color:var(--amber)}
.d-alert.ok .d-alert-title{color:var(--teal)}
.d-alert.bad .d-alert-title{color:var(--red)}
.d-alert-body{font-size:12px;color:var(--text2);line-height:1.55}

/* ── INP ── */
.d-inp{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:var(--r-sm);padding:10px 13px;font-family:'DM Sans',sans-serif;font-size:13px;color:var(--text);outline:none;transition:border-color .15s}
.d-inp:focus{border-color:var(--teal)}
.d-inp::placeholder{color:var(--text3)}
select.d-inp{cursor:pointer}
.d-inp-lbl{font-size:10px;font-weight:600;color:var(--text2);letter-spacing:.4px;text-transform:uppercase;margin-bottom:5px}

/* ── HELPER TIP ── */
.d-helper{font-size:12px;color:var(--text2);line-height:1.65;padding:11px 13px;background:var(--bg2);border-radius:var(--r-sm);border-left:3px solid var(--teal);margin-bottom:12px}
.d-divider{height:1px;background:var(--border);margin:18px 0}

/* ── TIER ── */
.d-tier{display:flex;align-items:center;gap:12px;padding:11px 14px;border-radius:var(--r-sm);background:var(--bg2);border:1px solid var(--border);margin-bottom:7px}
.d-tier-icon{font-size:24px}
.d-tier-name{font-size:13px;font-weight:700}
.d-tier-pts{font-size:11px;color:var(--text2);margin-top:1px}

/* ── PROFILE ── */
.d-prof-row{display:flex;align-items:center;justify-content:space-between;padding:11px 0;border-bottom:1px solid var(--border)}
.d-prof-row:last-child{border:none}
.d-prof-lbl{font-size:13px;color:var(--text2)}
.d-prof-val{font-size:13px;font-weight:600}

/* ── MOBILE TOPBAR ── */
.d-mobile-topbar{display:none;align-items:center;justify-content:space-between;padding:14px 16px 0;margin-bottom:16px}
.d-mobile-logo{display:flex;align-items:center;gap:8px}
.d-mobile-logo-icon{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,var(--teal),var(--teal2));display:flex;align-items:center;justify-content:center;font-size:16px}
.d-mobile-logo-name{font-family:'Syne',sans-serif;font-weight:900;font-size:15px}
.d-mobile-logo-name em{color:var(--teal);font-style:normal}
.d-mobile-btns{display:flex;align-items:center;gap:8px}
.d-mobile-theme-btn{background:var(--bg3);border:1px solid var(--border);border-radius:20px;padding:5px 10px;font-size:14px;cursor:pointer;transition:background .2s,border-color .2s;display:flex;align-items:center}
.d-mobile-theme-btn:hover{border-color:var(--border2)}
.d-mobile-country-btn{background:var(--bg3);border:1px solid var(--border);border-radius:20px;padding:5px 11px;font-size:12px;font-weight:600;color:var(--text);cursor:pointer;display:flex;align-items:center;gap:5px}

/* ── BOTTOM NAV (mobile) ── */
.d-bottom-nav{display:none;position:fixed;bottom:0;left:0;right:0;background:var(--bg1);border-top:1px solid var(--border);z-index:100;padding:6px 0 10px;box-shadow:0 -8px 32px rgba(0,0,0,.4)}
.d-bottom-nav-inner{display:flex;align-items:flex-end;justify-content:space-around}
.d-bn-item{display:flex;flex-direction:column;align-items:center;gap:3px;cursor:pointer;padding:4px 6px;border-radius:10px;transition:all .15s;flex:1;position:relative}
.d-bn-item.active .d-bn-icon svg{stroke:var(--teal)}
.d-bn-item.active .d-bn-label{color:var(--teal)}
.d-bn-icon{width:26px;height:26px;display:flex;align-items:center;justify-content:center}
.d-bn-icon svg{width:20px;height:20px;stroke:var(--text2);fill:none;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round;transition:.15s}
.d-bn-label{font-size:9px;color:var(--text2);font-weight:600;transition:.15s;white-space:nowrap}
.d-bn-dot{position:absolute;top:2px;right:8px;width:6px;height:6px;border-radius:50%;background:var(--teal)}

/* ── COUNTRY SHEET (mobile) ── */
.d-sheet-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:190}
.d-sheet-overlay.open{display:block}
.d-country-sheet{display:none;position:fixed;bottom:0;left:0;right:0;background:var(--bg1);border-top:1px solid var(--border);border-radius:20px 20px 0 0;padding:20px 16px 48px;z-index:200;animation:d-slideUp .25s ease}
.d-country-sheet.open{display:block}
.d-sheet-handle{width:36px;height:4px;background:var(--bg4);border-radius:2px;margin:0 auto 18px}
.d-sheet-title{font-family:'Syne',sans-serif;font-size:15px;font-weight:800;margin-bottom:14px}
.d-country-option{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-radius:var(--r-sm);cursor:pointer;margin-bottom:6px;border:1px solid var(--border);background:var(--bg2);transition:all .15s}
.d-country-option:hover,.d-country-option.selected{border-color:var(--border2);background:var(--teal-dim)}
.d-country-option-name{font-size:14px;font-weight:600}
.d-country-option-cities{font-size:11px;color:var(--text2);margin-top:2px}

/* ── LOGOUT BTN ── */
.d-logout-btn{display:flex;align-items:center;gap:7px;padding:10px 16px;font-size:12px;font-weight:600;color:var(--text3);cursor:pointer;transition:color .15s;border-top:1px solid var(--border)}
.d-logout-btn:hover{color:var(--red)}

/* ── MOBILE BREAKPOINTS ── */
.d-desktop-only{display:block}.d-mobile-only{display:none}
@media(max-width:768px){.d-desktop-only{display:none!important}.d-mobile-only{display:block!important}

  html,body{overflow:hidden}
  .d-sidebar{display:none}
  .d-bottom-nav{display:block}
  .d-mobile-topbar{display:flex}
  .d-shell{flex-direction:column}
  .d-main{padding-bottom:70px}
  .d-page{padding:0 14px 90px}
  .d-topbar{margin-bottom:16px;align-items:center}
  .d-page-title{font-size:19px}
  .d-topbar-right{display:none}
  .d-g4{grid-template-columns:repeat(2,1fr);gap:9px}
  .d-g3{grid-template-columns:repeat(2,1fr)}
  .d-g2{grid-template-columns:repeat(2,1fr);gap:9px}
  .d-g21{grid-template-columns:1fr}
  .d-g12{grid-template-columns:1fr}
  .d-sc{padding:12px 14px}
  .d-sc-val{font-size:20px}
  .d-voice-box{padding:16px;border-radius:var(--r-lg)}
  .d-voice-title{font-size:14px}
  .d-ring-wrap{width:110px;height:110px}
  .d-ring-wrap svg{width:110px;height:110px}
  .d-ring-val{font-size:24px}
  .d-quiz-opts{grid-template-columns:1fr}
  .d-quiz-wrap{padding:18px}
  .d-quiz-q{font-size:15px;margin:14px 0 16px}
  .d-card,.d-card-teal,.d-card-amber,.d-card-red,.d-card-blue{padding:14px}
  .d-ep-card{padding:10px}
  .d-ep-thumb{width:44px;height:44px;font-size:18px}
  .d-city-tabs{gap:4px}
  .d-city-tab{padding:4px 10px;font-size:10px}
  .d-joint-card{flex-wrap:wrap}
  .d-map-body{padding:12px 14px}
}

@media(min-width:769px){
  .d-mobile-topbar{display:none!important}
  .d-bottom-nav{display:none!important}
  .d-country-sheet{display:none!important}
  .d-sheet-overlay{display:none!important}
}
`;

/* ─── DATA ─────────────────────────────────────────────────── */
const COUNTRIES = [
  { code: "GH", flag: "🇬🇭", name: "Ghana", currSymbol: "₵",   cities: ["Accra", "Kumasi", "Takoradi", "Tamale"] },
  { code: "NG", flag: "🇳🇬", name: "Nigeria", currSymbol: "₦", cities: ["Lagos", "Abuja", "Port Harcourt", "Kano"] },
  { code: "SN", flag: "🇸🇳", name: "Senegal", currSymbol: "CFA", cities: ["Dakar", "Thiès", "Saint-Louis"] },
  { code: "CI", flag: "🇨🇮", name: "Côte d'Ivoire", currSymbol: "CFA", cities: ["Abidjan", "Bouaké", "Yamoussoukro"] },
  { code: "CM", flag: "🇨🇲", name: "Cameroon", currSymbol: "CFA", cities: ["Douala", "Yaoundé", "Bamenda"] },
];

const FOOD_DB = {
  GH: [
    {
      name: "Ga Kenkey",
      gi: 41,
      carbs: 35,
      portion: "1 fist-sized",
      why: "Fermentation lowers GI — very stable",
      emoji: "🌽",
    },
    {
      name: "Processed Fufu",
      gi: 31,
      carbs: 30,
      portion: "1 small ball",
      why: "Lowest GI Ghanaian staple",
      emoji: "🍚",
    },
    {
      name: "Boiled Unripe Plantain",
      gi: 45,
      carbs: 25,
      portion: "1 medium",
      why: "Resistant starch — steady all day",
      emoji: "🍌",
    },
    {
      name: "Beans Stew",
      gi: 42,
      carbs: 20,
      portion: "1 cup",
      why: "High fibre slows glucose entry",
      emoji: "🫘",
    },
    {
      name: "Okra Stew",
      gi: 15,
      carbs: 10,
      portion: "Generous serving",
      why: "Mucilage fibre slows carb absorption",
      emoji: "🥗",
    },
    {
      name: "Grilled Tilapia",
      gi: 0,
      carbs: 0,
      portion: "1 medium",
      why: "Pure protein — zero carb impact",
      emoji: "🐟",
    },
  ],
  NG: [
    {
      name: "Moi Moi",
      gi: 35,
      carbs: 22,
      portion: "1 medium wrap",
      why: "Steamed bean pudding — slower release",
      emoji: "🟤",
    },
    {
      name: "Bitter Leaf Soup",
      gi: 18,
      carbs: 8,
      portion: "1 bowl",
      why: "Extremely low GI",
      emoji: "🥬",
    },
    {
      name: "Boiled Plantain",
      gi: 45,
      carbs: 25,
      portion: "1 medium",
      why: "Resistant starch",
      emoji: "🍌",
    },
    {
      name: "Egusi Soup",
      gi: 25,
      carbs: 12,
      portion: "1 bowl",
      why: "Seeds + veg = low GI",
      emoji: "🥣",
    },
    {
      name: "Ewa Agoyin",
      gi: 40,
      carbs: 22,
      portion: "½ cup",
      why: "Beans naturally low GI",
      emoji: "🫘",
    },
    {
      name: "Grilled Catfish",
      gi: 0,
      carbs: 0,
      portion: "1 medium",
      why: "Pure protein",
      emoji: "🐟",
    },
  ],
  SN: [
    {
      name: "Thiéboudienne (light)",
      gi: 50,
      carbs: 40,
      portion: "Small plate",
      why: "Less rice + more fish lowers GI",
      emoji: "🍛",
    },
    {
      name: "Yassa Poulet",
      gi: 30,
      carbs: 15,
      portion: "1 serving",
      why: "Lemon + onion + low-GI vegetables",
      emoji: "🍗",
    },
    {
      name: "Niébé (Cowpea)",
      gi: 33,
      carbs: 18,
      portion: "1 cup",
      why: "High fibre legume",
      emoji: "🫘",
    },
    {
      name: "Millet Couscous",
      gi: 44,
      carbs: 28,
      portion: "½ cup",
      why: "Slower glucose release than rice",
      emoji: "🌾",
    },
    {
      name: "Grilled Thiof",
      gi: 0,
      carbs: 0,
      portion: "1 medium",
      why: "Pure protein",
      emoji: "🐟",
    },
  ],
  CI: [
    {
      name: "Attiéké",
      gi: 40,
      carbs: 30,
      portion: "½ cup",
      why: "Fermented cassava lowers GI",
      emoji: "🥣",
    },
    {
      name: "Kedjenou Chicken",
      gi: 22,
      carbs: 10,
      portion: "1 serving",
      why: "Low-GI stew vegetables",
      emoji: "🍗",
    },
    {
      name: "Foutou",
      gi: 48,
      carbs: 32,
      portion: "1 small ball",
      why: "Pair with vegetables to lower impact",
      emoji: "🍠",
    },
    {
      name: "Grilled Tilapia",
      gi: 0,
      carbs: 0,
      portion: "1 medium",
      why: "Pure protein",
      emoji: "🐟",
    },
  ],
  CM: [
    {
      name: "Ndolé",
      gi: 15,
      carbs: 6,
      portion: "1 bowl",
      why: "Bitter greens + groundnuts — very low GI",
      emoji: "🥬",
    },
    {
      name: "Eru Soup",
      gi: 12,
      carbs: 5,
      portion: "1 bowl",
      why: "Shredded greens in palm oil",
      emoji: "🥗",
    },
    {
      name: "Bobolo",
      gi: 38,
      carbs: 22,
      portion: "1 stick",
      why: "Fermentation lowers cassava GI",
      emoji: "🟡",
    },
    {
      name: "Boiled Plantain",
      gi: 45,
      carbs: 25,
      portion: "1 medium",
      why: "Resistant starch",
      emoji: "🍌",
    },
  ],
};

const FOOD_JOINTS = {
  Accra: [
    {
      name: "Mama's Kenkey Spot",
      food: "Ga Kenkey + Okra + Tilapia",
      gi: 41,
      dist: "0.4 km",
      mins: "6 min",
      traffic: "clear",
      note: "Closest, traffic clear on Ring Road",
      emoji: "🌽",
      area: "Lapaz",
      best: true,
    },
    {
      name: "Auntie Ama's Chop Bar",
      food: "Processed Fufu + Light Soup",
      gi: 31,
      dist: "1.1 km",
      mins: "12 min",
      traffic: "moderate",
      note: "Moderate traffic on Spintex Rd",
      emoji: "🍚",
      area: "East Legon",
      best: false,
    },
    {
      name: "Circle Roadside",
      food: "Beans Stew + Plantain",
      gi: 42,
      dist: "3.2 km",
      mins: "35 min",
      traffic: "heavy",
      note: "⚠️ Heavy traffic toward Circle — avoid",
      emoji: "🫘",
      area: "Nima",
      best: false,
    },
  ],
  Lagos: [
    {
      name: "Mama Titi's Buka",
      food: "Moi Moi + Egusi + Fish",
      gi: 30,
      dist: "0.6 km",
      mins: "8 min",
      traffic: "clear",
      note: "Yaba area, traffic clear now",
      emoji: "🟤",
      area: "Yaba",
      best: true,
    },
    {
      name: "Tantalizers Ikeja",
      food: "Grilled Catfish + Veg",
      gi: 18,
      dist: "2.3 km",
      mins: "28 min",
      traffic: "heavy",
      note: "⚠️ Allen Ave very heavy — wait 1 hour",
      emoji: "🐟",
      area: "Ikeja",
      best: false,
    },
  ],
  Kumasi: [
    {
      name: "Kejetia Chop Bar",
      food: "Fufu + Groundnut Soup",
      gi: 31,
      dist: "0.8 km",
      mins: "9 min",
      traffic: "clear",
      note: "Adum area, traffic clear",
      emoji: "🍚",
      area: "Adum",
      best: true,
    },
  ],
  Dakar: [
    {
      name: "Plateau Restaurant",
      food: "Yassa Poulet + Millet",
      gi: 35,
      dist: "0.9 km",
      mins: "10 min",
      traffic: "clear",
      note: "Plateau area, clear roads",
      emoji: "🍗",
      area: "Plateau",
      best: true,
    },
  ],
  Abuja: [
    {
      name: "Wuse Market Kitchen",
      food: "Moi Moi + Bitter Leaf",
      gi: 25,
      dist: "1.2 km",
      mins: "10 min",
      traffic: "clear",
      note: "Wuse 2, traffic clear",
      emoji: "🥬",
      area: "Wuse 2",
      best: true,
    },
  ],
};

const AI_RESPONSES = {
  GH: {
    lunch:
      "🍽️ For lunch: <strong>1 fist-sized Ga Kenkey + generous okra stew + 1 grilled tilapia</strong> — ~35g carbs, GI 41. Eat by <strong>1:30 PM</strong>. <strong>Nearest: Mama's Kenkey, Lapaz (0.4km, traffic clear)</strong>. Avoid Circle route — heavy gridlock right now.",
    snack:
      "🥜 Best snack: <strong>small handful of groundnuts (GI 14)</strong> + 1 boiled egg. Available at most roadside stalls. Keeps you full 2–3 hours without any spike.",
    kenkey:
      "✅ Ga Kenkey GI = <strong>41</strong>. Fermentation by natural bacteria transforms the starch. Pair with okra stew (GI 15) for a very stable meal.",
    fufu: "📊 Processed fufu GI = <strong>31</strong> (lowest in Ghana). Pounded fufu = 55. Always choose processed when available.",
  },
  NG: {
    lunch:
      "🍽️ For lunch: <strong>Moi Moi + Bitter Leaf Soup + Grilled Catfish</strong> — ~30g carbs, GI ~28. Eat by <strong>1:00 PM</strong>. <strong>Nearest: Mama Titi's Buka, Yaba (0.6km, clear)</strong>. Avoid Third Mainland and Allen Ave — heavy gridlock.",
    snack:
      "🥜 Best Lagos snack: <strong>garden egg + groundnut paste</strong>. Very low GI. Avoid chin chin, puff puff, and sugary zobo.",
    fufu: "📊 Eba (garri) GI = <strong>87</strong> — very high. Prefer Moi Moi (GI 35) or beans-based meals instead.",
    jollof:
      "⚠️ Jollof rice GI = <strong>65–72</strong>. If you eat it, take a <strong>small portion + extra protein</strong>. Check your reading 2 hours after.",
  },
  SN: {
    lunch:
      "🍽️ For lunch: <strong>Yassa Poulet + ½ cup Niébé</strong> — ~28g carbs, GI ~32. Eat by <strong>1:30 PM</strong>. Nearest: Plateau Restaurant (0.9km, traffic clear).",
    snack:
      "🥜 Best Dakar snack: <strong>Niébé boiled plain</strong>, or grilled thiof with lemon. Avoid sweet bissap with added sugar.",
    thiebou:
      "📊 Thiéboudienne: request <strong>less rice + more fish + vegetables</strong> and the meal GI drops from ~68 to ~45.",
  },
};

const SUGAR_LOGS = [
  { time: "7:30 AM", val: 5.4, meal: "Before breakfast", status: "normal" },
  { time: "9:15 AM", val: 7.8, meal: "After breakfast", status: "high" },
  { time: "1:00 PM", val: 6.2, meal: "Before lunch", status: "normal" },
  { time: "3:30 PM", val: 6.9, meal: "After lunch", status: "normal" },
];

const EPISODES = [
  {
    num: 14,
    title: "Why West African Men Are Dying From Diabetes in Silence",
    emoji: "🎙",
    views: "6.1k",
    bg: "#080818",
  },
  {
    num: 13,
    title: "Lagos Traffic + Diabetes: How to Protect Your Sugar Levels",
    emoji: "🚗",
    views: "4.8k",
    bg: "#180808",
  },
  {
    num: 12,
    title: "The Pregnancy Trap: What Nobody Tells Diabetic Mothers in Africa",
    emoji: "💕",
    views: "3.2k",
    bg: "#180818",
  },
  {
    num: 11,
    title: "Kenkey, Moi Moi, Attiéké: Which West African Staple is Safest?",
    emoji: "🌍",
    views: "7.4k",
    bg: "#081808",
  },
  {
    num: 10,
    title: "Your Child Has Diabetes — What to Do in the Next 24 Hours",
    emoji: "🧒",
    views: "3.8k",
    bg: "#081018",
  },
];

const QUIZ_QS = [
  {
    q: "Which staple has the LOWEST glycemic index across West Africa?",
    opts: [
      { label: "White Rice", sub: "GI 73", correct: false },
      { label: "Processed Fufu", sub: "GI 31 🇬🇭", correct: true },
      { label: "Pounded Yam", sub: "GI 65", correct: false },
      { label: "Eba (Garri)", sub: "GI 87", correct: false },
    ],
    explain:
      "Processed/powdered fufu (Ghana) has GI 31 — the lowest of any common West African staple. Compare this to eba at GI 87 — nearly 3× higher.",
  },
  {
    q: "Lagos traffic is heavy. Your blood sugar is 6.2 mmol/L and you haven't eaten. What should you do?",
    opts: [
      {
        label: "Wait in traffic and eat when you arrive",
        sub: "Risky",
        correct: false,
      },
      {
        label: "Find nearest chop bar and eat first",
        sub: "Correct ✓",
        correct: true,
      },
      { label: "Skip the meal entirely", sub: "Dangerous", correct: false },
      {
        label: "Drink a malt drink to hold on",
        sub: "Will spike sugar",
        correct: false,
      },
    ],
    explain:
      "Delayed meals cause blood sugar to drop then spike sharply when you finally eat. Always eat before a long journey. A malt drink (GI 70+) will spike your sugar badly.",
  },
];

/* ─── HELPERS ─────────────────────────────────────────────── */
function GiChip({ gi }) {
  const cls =
    gi === 0
      ? "d-chip-teal"
      : gi <= 40
      ? "d-chip-teal"
      : gi <= 60
      ? "d-chip-amber"
      : "d-chip-red";
  return (
    <span className={`d-chip ${cls}`}>
      {gi === 0 ? "No carbs" : `GI ${gi}`}
    </span>
  );
}

function TrafficPill({ level }) {
  const m = {
    clear: ["d-traffic-clear", "✓ Clear"],
    moderate: ["d-traffic-mod", "~ Moderate"],
    heavy: ["d-traffic-heavy", "✕ Heavy"],
  };
  const [cls, lbl] = m[level] || m.clear;
  return <span className={`d-traffic-pill ${cls}`}>{lbl}</span>;
}

function BarChart({ data, colors }) {
  const max = Math.max(...data, 1);
  return (
    <div className="d-bars">
      {data.map((v, i) => (
        <div
          key={i}
          className={`d-bar ${colors[i] || "d-bt"}`}
          style={{ height: `${(v / max) * 100}%` }}
        />
      ))}
    </div>
  );
}

/* ─── PAGES ───────────────────────────────────────────────── */

// ─────────────────────────────────────────────────────────────
// ALL 6 REAL-DATA PAGES — wired to exact backend response shapes
// Backend response keys mapped precisely from the route files
// MGL2390 · In memory of Mina Gbemisola LAAK
// ─────────────────────────────────────────────────────────────

// ── RESPONSE SHAPE REFERENCE ──────────────────────────────────
// GET  /api/auth/profile    → { profile: { id, first_name, last_name, email, phone, country, city, diabetes_type, target_low, target_high, insulin_type, daily_dose, tokens, streak_days } }
// GET  /api/sugar/logs      → { logs: [{ id, value, unit, timing, status, notes, logged_at }], count }
// GET  /api/sugar/stats     → { stats: { average, minimum, maximum, timeInRange, hba1cEstimate, trend, totalReadings, lastReading }, prediction }
// POST /api/ai/meal-plan    → { response: "...", foods: [...], country, city }
// GET  /api/nearby/food     → { joints: [{ name, address, rating, reviewCount, isOpen, distance, travelTime, trafficLevel, trafficNote, recommended, giNote }], aiRecommendation }
// GET  /api/alerts/history  → { alerts: [{ id, alert_type, sugar_value, message, channel, status, sent_at }] }
// GET  /api/contacts        → { contacts: [{ id, name, phone, whatsapp, relationship, is_primary }] }
// POST /api/contacts        → { message, contact }
// DELETE /api/contacts/:id  → { message }
// GET  /api/quiz/stats      → { totalPlayed, correctAnswers, accuracy, totalTokens, streakDays }
// POST /api/quiz/submit     → { correct, tokensEarned, totalTokens, streak, message }
// POST /api/alerts/send     → { message, results, alertSent }
// PUT  /api/auth/profile    → { message, profile }

// ═══════════════════════════════════════════════════════════════
// HOME PAGE — real profile, real stats, real Claude AI
// ═══════════════════════════════════════════════════════════════
function HomePage({ country, city, onNav }) {
  const curr = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];

  // profile from GET /api/auth/profile → { profile: {...} }
  const [profile, setProfile] = useState(api.auth.getUser());
  // stats from GET /api/sugar/stats → { stats: { average, timeInRange, trend, totalReadings, hba1cEstimate }, prediction }
  const [stats, setStats] = useState(null);
  // logs from GET /api/sugar/logs → { logs: [{ value, unit, status, timing, logged_at }] }
  const [logs, setLogs] = useState([]);
  // alerts from GET /api/alerts/history → { alerts: [{ alert_type, sugar_value, message, sent_at }] }
  const [alertHist, setAlertHist] = useState([]);

  // AI
  const [aiText, setAiText]       = useState("");
  const [aiVisible, setAiVisible] = useState(false);
  const [aiLoading, setAiLoading]   = useState(false);
  const [customQ, setCustomQ]       = useState("");
  const [listening, setListening]   = useState(false);
  const [speaking, setSpeaking]     = useState(false);
  const [userGPS, setUserGPS]       = useState(null);   // real GPS coords
  const [gpsLabel, setGpsLabel]     = useState(null);   // human readable area
  const recognitionRef              = useRef(null);
  const synthRef                    = useRef(window.speechSynthesis);

  // Get real GPS on mount — used for AI location context
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setUserGPS({ lat, lng });
        // Reverse geocode to get neighbourhood name using free API
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=14`,
            { headers: { "Accept-Language": "en" } }
          );
          const d = await r.json();
          // Extract neighbourhood or suburb
          const label = d.address?.suburb
            || d.address?.neighbourhood
            || d.address?.quarter
            || d.address?.city_district
            || d.address?.town
            || d.address?.city
            || city;
          setGpsLabel(label);
        } catch { setGpsLabel(city); }
      },
      () => { setGpsLabel(city); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // ── Voice input via Web Speech API ─────────────────────────────────────────
  function startListening() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setCustomQ("Voice input not supported in this browser. Please use Chrome.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const rec = new SpeechRecognition();
    recognitionRef.current = rec;
    rec.lang = "en-GH"; // Ghana English — falls back to en-US if unsupported
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart  = () => setListening(true);
    rec.onend    = () => setListening(false);
    rec.onerror  = (e) => {
      setListening(false);
      if (e.error === "not-allowed") {
        setCustomQ("Microphone access denied. Please allow microphone in browser settings.");
      }
    };

    rec.onresult = (e) => {
      // Build transcript from all results
      let interim = "";
      let final   = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      setCustomQ(final || interim);
    };

    rec.start();
  }

  // ── Text-to-speech — reads AI response in West African English ─────────────
  function speakResponse(text) {
    const synth = synthRef.current;
    if (!synth) return;
    synth.cancel(); // stop any ongoing speech

    if (speaking) { setSpeaking(false); return; } // tap again to stop

    const utterance = new SpeechSynthesisUtterance(text);

    // Pick the best available voice — prefer en-GH, en-NG, en-ZA, then any English
    const voices = synth.getVoices();
    const preferred = ["en-GH", "en-NG", "en-ZA", "en-KE", "en-TZ", "en-NG"];
    let chosenVoice = null;
    for (const lang of preferred) {
      chosenVoice = voices.find(v => v.lang === lang);
      if (chosenVoice) break;
    }
    // Fallback: any English voice
    if (!chosenVoice) chosenVoice = voices.find(v => v.lang.startsWith("en"));
    if (chosenVoice) utterance.voice = chosenVoice;

    // Voice characteristics for a warm West African delivery
    utterance.rate  = 0.92;  // slightly slower — clear articulation
    utterance.pitch = 1.05;  // slightly higher — warm and friendly
    utterance.volume = 1.0;

    utterance.onstart = () => setSpeaking(true);
    utterance.onend   = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);

    synth.speak(utterance);
  }
  const [notifPerm, setNotifPerm] = useState(() =>
    ("Notification" in window) ? Notification.permission : "unsupported"
  );
  const { requestPermission } = useCheckReminder(logs);
  async function enableReminders() {
    const ok = await requestPermission();
    setNotifPerm(ok ? "granted" : "denied");
  }

  useEffect(() => {
    api.auth.getProfile()
      .then(r => { if (r.profile) setProfile(r.profile); })
      .catch(() => {});
    api.sugar.getStats()
      .then(r => { if (r.stats) setStats(r.stats); })
      .catch(() => {});
    api.sugar.getLogs(7)
      .then(r => setLogs(r.logs || []))
      .catch(() => {});
    api.alerts.getHistory()
      .then(r => setAlertHist(r.alerts || []))
      .catch(() => {});
  }, []); // runs once — blood sugar data belongs to user, not country

  // When country changes — clear AI response so next chip gives fresh country-specific advice
  useEffect(() => {
    setAiVisible(false);
    setAiText("");
    setCustomQ("");
  }, [country]);

  // Quick-tap chips send real queries to Claude
  const CHIPS = {
    GH: [
      { l: "🍽️ Plan my lunch",  q: `Plan my lunch in ${city} under 30 cedis. Give a specific low-GI Ghanaian meal with exact GI score and portion size.` },
      { l: "🥜 Best snack",      q: "What is the best low-GI roadside snack in Ghana for a diabetic? Give GI score." },
      { l: "🌽 Kenkey safe?",    q: "Is Ga kenkey safe for a Type 2 diabetic? What is its glycemic index and recommended portion?" },
      { l: "🍚 Fufu GI",        q: "Compare processed fufu vs pounded fufu for diabetes. Which has the lower GI?" },
    ],
    NG: [
      { l: "🍽️ Plan my lunch",  q: `Plan my lunch in Lagos under 2000 naira. Low-GI Nigerian meal with exact GI score.` },
      { l: "🥜 Best snack",      q: "Best low-GI street snack for a diabetic in Lagos right now? Give GI score." },
      { l: "⚠️ Eba risk?",      q: "Is eba (garri) dangerous for a diabetic? What is its GI and what should I eat instead?" },
      { l: "🍛 Jollof safe?",   q: "Is jollof rice safe for a Type 2 diabetic? Give the GI and portion advice." },
    ],
    SN: [
      { l: "🍽️ Plan my lunch",  q: `Plan my lunch in Dakar. Give a low-GI Senegalese meal with GI score.` },
      { l: "🥜 Best snack",      q: "Best low-GI snack for a diabetic in Dakar?" },
      { l: "🍛 Thiébou risk?",  q: "Is thiéboudienne safe for a diabetic? How can I order it to reduce the GI?" },
    ],
    CI: [
      { l: "🍽️ Plan my lunch",  q: `Plan my lunch in Abidjan. Low-GI Ivorian meal with GI score.` },
      { l: "🍽️ Attiéké GI",    q: "What is the glycemic index of attiéké? Is it safe for a diabetic and what portion?" },
    ],
    CM: [
      { l: "🍽️ Plan my lunch",  q: `Plan my lunch in Douala. Low-GI Cameroonian meal with GI score.` },
      { l: "🥬 Ndolé safe?",    q: "Is ndolé safe for a diabetic? What makes it a good or bad choice?" },
    ],
  };
  const chips = CHIPS[country] || CHIPS.GH;

  async function fireAI(query) {
    const q = (query || customQ).trim();
    if (!q || aiLoading) return;

    if (!api.auth.isLoggedIn()) {
      setAiText("⚠️ Please sign in to use ChopTime AI Voice Advisor.");
      setAiVisible(true);
      return;
    }

    setAiLoading(true);
    setAiVisible(false);
    setCustomQ("");
    synthRef.current?.cancel(); // stop any reading when new question fires

    const lastLog = logs[0];
    const lastVal = lastLog
      ? (lastLog.unit === "mg/dL" ? parseFloat(lastLog.value) / 18 : parseFloat(lastLog.value))
      : null;

    // Use real GPS neighbourhood if available, otherwise profile city
    const locationLabel = gpsLabel || city;

    try {
      // Pass real GPS + neighbourhood to backend for accurate food spot recommendations
      const r = await api.ai.mealPlan(
        q, country, locationLabel, lastVal, "medium",
        userGPS?.lat, userGPS?.lng
      );
      const response = r.response || "Here is my advice for you...";
      setAiText(response);
      setAiVisible(true);
      // Auto-read the response
      setTimeout(() => speakResponse(response), 300);
    } catch (e) {
      const msg = e.message || "";
      if (msg.includes("Not logged in") || msg.includes("session")) {
        setAiText("⚠️ Session expired. Please refresh and sign in again.");
      } else if (msg.includes("limit")) {
        setAiText("⚠️ AI request limit reached. Please wait a moment.");
      } else if (msg.includes("connect") || msg.includes("fetch")) {
        setAiText("⚠️ Server waking up — please wait 30 seconds and try again.");
      } else {
        setAiText("⚠️ " + (msg || "Could not reach ChopTime AI. Please try again."));
      }
      setAiVisible(true);
    } finally {
      setAiLoading(false);
    }
  }

  // ── Derive display values from exact backend field names ──
  const latestLog = logs[0];
  // sugar_logs.value + sugar_logs.unit
  const latestMmol = latestLog
    ? (latestLog.unit === "mg/dL" ? parseFloat(latestLog.value) / 18 : parseFloat(latestLog.value))
    : null;
  // sugar_logs.status = 'low' | 'normal' | 'high' (set by backend)
  const latestStatus = latestLog?.status || (latestMmol == null ? "normal" : latestMmol < 4 ? "low" : latestMmol > 7.8 ? "high" : "normal");

  // stats.average, stats.timeInRange, stats.totalReadings, stats.hba1cEstimate, stats.trend
  const avgDisplay    = stats?.average || "—";
  const inRange       = stats?.timeInRange ?? null;
  const totalReadings = stats?.totalReadings ?? logs.length;
  const trendText     = stats?.trend || null;

  const ringColor  = latestStatus === "normal" ? "var(--teal)" : latestStatus === "high" ? "var(--amber)" : "var(--red)";
  const ringPct    = latestMmol ? Math.min(latestMmol / 15, 1) : 0;
  const ringOffset = 314.16 - (ringPct * 314.16 * 0.78) + 35;

  const barData   = logs.length >= 2
    ? logs.slice(0, 7).map(l => parseFloat(l.unit === "mg/dL" ? l.value / 18 : l.value)).reverse()
    : [5.4, 7.2, 6.1, 8.0, 5.9, 6.8, 6.2];
  const barColors = barData.map(v => v > 7.8 ? "d-br" : v > 7 ? "d-ba" : "d-bt");

  // profile.first_name from profiles table
  const cachedUser  = api.auth.getUser();
  const firstName   = profile?.first_name || cachedUser?.first_name || cachedUser?.firstName || cachedUser?.email?.split("@")[0] || "there";
  const today       = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Welcome, {firstName} 👋</div>
          <div className="d-page-sub">{curr.flag} {curr.name} · {city} · {today}</div>
        </div>
        <div className="d-topbar-right">
          <button className="d-btn d-btn-ghost" onClick={() => onNav("tracker")}>+ Log Sugar</button>
          <button className="d-btn d-btn-teal" onClick={() => onNav("nearby")}>📍 Find Food</button>
        </div>
      </div>

      {/* Danger alert if latest reading is dangerous */}
      {latestMmol != null && (latestMmol < 3.9 || latestMmol > 13) && (
        <div className={`d-alert ${latestMmol < 3.9 ? "bad" : "warn"}`} style={{ marginBottom: 14 }}>
          <div className="d-alert-icon">{latestMmol < 3.9 ? "🚨" : "⚠️"}</div>
          <div>
            <div className="d-alert-title">
              {latestMmol < 3.9 ? "Hypoglycemia — Act Now!" : "Dangerously High Blood Sugar!"}
            </div>
            <div className="d-alert-body">
              {latestMmol < 3.9
                ? `Reading: ${latestMmol.toFixed(1)} mmol/L. Take sugar, glucose water, or juice immediately.`
                : `Reading: ${latestMmol.toFixed(1)} mmol/L. Drink water, avoid carbs, see your doctor.`}
              {" "}Your emergency contacts have been notified via WhatsApp.
            </div>
          </div>
        </div>
      )}

      {/* KPI cards — all real data */}
      <div className="d-g4">
        {[
          {
            lbl: "Latest Reading",
            val: latestMmol != null ? latestMmol.toFixed(1) : "—",
            unit: "mmol/L",
            delta: latestMmol != null ? `● ${latestStatus}` : "No readings yet",
            cls: latestStatus === "normal" ? "t" : "r",
            up: latestStatus === "normal",
          },
          {
            lbl: "30-Day Average",
            val: avgDisplay,
            unit: "mmol/L",
            delta: trendText ? `Trend: ${trendText}` : "past 30 days",
            cls: "",
            up: true,
          },
          {
            lbl: "Time in Range",
            val: inRange != null ? `${inRange}%` : "—",
            unit: "",
            delta: "Target: >70%",
            cls: inRange != null && inRange >= 70 ? "t" : inRange != null ? "r" : "",
            up: inRange != null && inRange >= 70,
          },
          {
            lbl: "Total Readings",
            val: `${totalReadings}`,
            unit: "logs",
            delta: stats?.hba1cEstimate ? `HbA1c ~${stats.hba1cEstimate}%` : "lifetime",
            cls: "",
            up: true,
          },
        ].map((s, i) => (
          <div className={`d-sc ${s.cls}`} key={i}>
            <div className="d-sc-lbl">{s.lbl}</div>
            <div className={`d-sc-val ${s.cls}`}>
              {s.val}
              <span style={{ fontSize: 11, fontWeight: 400, color: "var(--text2)", marginLeft: 3 }}>{s.unit}</span>
            </div>
            <div className={`d-sc-delta ${s.up ? "up" : "dn"}`}>{s.delta}</div>
          </div>
        ))}
      </div>

      {/* AI Meal Advisor — POST /api/ai/meal-plan */}
      <div className="d-voice-box">
        <div className="d-voice-hdr">
          {/* Mic button — tap to record voice, tap again to stop */}
          <div
            className={`d-mic-btn ${listening ? "on" : ""}`}
            onClick={startListening}
            title={listening ? "Tap to stop recording" : "Tap to speak your question"}
            style={{
              background: listening
                ? "rgba(255,82,82,.2)"
                : "rgba(0,212,160,.1)",
              borderColor: listening
                ? "rgba(255,82,82,.6)"
                : "rgba(0,212,160,.35)",
              position: "relative",
            }}
          >
            {listening ? (
              /* Recording indicator */
              <svg viewBox="0 0 24 24" style={{ stroke: "#ff5252" }}>
                <rect x="6" y="6" width="12" height="12" rx="2" fill="#ff5252" stroke="none" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
              </svg>
            )}
          </div>
          <div>
            <div className="d-voice-title">🌿 ChopTime Food Advisor</div>
            <div className="d-voice-sub">
              {gpsLabel
                ? `📍 ${gpsLabel} · tap mic to speak`
                : `${city} · tap mic to speak your question`}
            </div>
          </div>
          {/* Speaker button — reads AI response aloud */}
          {aiVisible && aiText && (
            <div
              onClick={() => speakResponse(aiText)}
              title={speaking ? "Tap to stop" : "Tap to hear response"}
              style={{
                width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                background: speaking ? "rgba(77,184,255,.2)" : "rgba(77,184,255,.1)",
                border: `2px solid ${speaking ? "rgba(77,184,255,.7)" : "rgba(77,184,255,.3)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                animation: speaking ? "d-pulse 1.2s ease-in-out infinite" : "none",
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                stroke="#4db8ff" strokeWidth="2" strokeLinecap="round">
                {speaking ? (
                  <><rect x="6" y="6" width="12" height="12" rx="2" fill="#4db8ff" stroke="none" /></>
                ) : (
                  <><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" /></>
                )}
              </svg>
            </div>
          )}
        </div>

        {/* Listening indicator */}
        {listening && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px", marginBottom: 8,
            background: "rgba(255,82,82,.08)",
            border: "1px solid rgba(255,82,82,.2)",
            borderRadius: 8, fontSize: 12,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff5252", animation: "d-pulse 1s ease infinite" }} />
            <span style={{ color: "#ff5252", fontWeight: 600 }}>Recording… speak now</span>
            <span style={{ color: "var(--text3)", marginLeft: "auto" }}>Tap mic to stop</span>
          </div>
        )}

        <div className="d-vchips">
          {chips.map((c) => (
            <div className="d-vchip" key={c.l} onClick={() => fireAI(c.q)}>{c.l}</div>
          ))}
        </div>

        {/* Text input + Ask button */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input
            className="d-inp"
            placeholder={listening ? "Listening…" : `Ask about food, diabetes, ${gpsLabel || city}…`}
            value={customQ}
            onChange={e => setCustomQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && fireAI()}
            style={{ flex: 1, opacity: listening ? .6 : 1 }}
            readOnly={listening}
          />
          <button
            className="d-btn d-btn-teal"
            onClick={() => fireAI()}
            disabled={aiLoading || (!customQ.trim() && !listening)}
          >
            {aiLoading ? "…" : "Ask"}
          </button>
        </div>

        {aiLoading && (
          <div style={{ fontSize: 12, color: "var(--teal)", fontStyle: "italic" }}>
            🌿 ChopTime is thinking…
          </div>
        )}
        {aiVisible && !aiLoading ? (
          <div className="d-ai-box show" style={{ whiteSpace: "pre-wrap", position: "relative" }}>
            {aiText}
            {/* Read aloud hint */}
            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 8 }}>
              🔊 Tap the speaker icon above to hear this read aloud
            </div>
          </div>
        ) : (
          !aiLoading && (
            <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>
              Tap 🎤 mic to speak · or type and tap Ask
            </div>
          )
        )}
      </div>

      {/* Notification Permission Banner */}
      {notifPerm !== "granted" && notifPerm !== "unsupported" && (
        <div style={{
          display:"flex", alignItems:"center", gap:12,
          padding:"12px 14px", marginBottom:14,
          background:"linear-gradient(135deg,#0d2a1a,#0a1f14)",
          border:"1px solid rgba(0,212,160,.25)", borderRadius:"var(--r-md)",
        }}>
          <div style={{ fontSize:24, flexShrink:0 }}>🔔</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--teal)", marginBottom:2 }}>
              Enable Sugar Check Reminders
            </div>
            <div style={{ fontSize:11, color:"var(--text2)", lineHeight:1.5 }}>
              {notifPerm === "denied"
                ? "Notifications blocked. Enable in browser settings."
                : "Get notified + phone vibration when it's time to log your blood sugar."}
            </div>
          </div>
          {notifPerm !== "denied" && (
            <button onClick={enableReminders} style={{
              flexShrink:0, padding:"7px 14px",
              background:"var(--teal)", color:"#06090b",
              border:"none", borderRadius:99,
              fontSize:12, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap",
            }}>Enable</button>
          )}
        </div>
      )}

      {/* ── MOBILE ONLY: Alerts + Quick Status ── */}
      <div className="d-mobile-only">
        <div className="d-sh"><span className="d-sh-title">Alerts & Predictions</span></div>
        {stats?.trend && (
          <div className={`d-alert ${stats.trend==="rising"?"warn":stats.trend==="falling"&&latestMmol<5?"bad":"ok"}`}>
            <div className="d-alert-icon">{stats.trend==="rising"?"📈":stats.trend==="falling"?"📉":"✅"}</div>
            <div>
              <div className="d-alert-title">{stats.trend==="rising"?"Sugar Trending Upward":stats.trend==="falling"?"Sugar Trending Downward":"Sugar Trend is Stable"}</div>
              <div className="d-alert-body">{stats.trend==="rising"?`Rising. Average ${avgDisplay} mmol/L. Reduce carbs, take a walk.`:stats.trend==="falling"?`Falling. ${latestMmol<5?"Have a protein snack now.":"Keep monitoring."}`:` Stable at ${avgDisplay} mmol/L.`}</div>
            </div>
          </div>
        )}
        {alertHist.length>0 ? alertHist.slice(0,1).map((a,i)=>(
          <div className={`d-alert ${a.alert_type==="low_sugar"?"bad":"warn"}`} key={i}>
            <div className="d-alert-icon">{a.alert_type==="low_sugar"?"🚨":"⚠️"}</div>
            <div>
              <div className="d-alert-title">{a.alert_type==="low_sugar"?"🚨 Low Sugar — Family Alerted":"⚠️ High Sugar — Family Alerted"}</div>
              <div className="d-alert-body">{a.sugar_value?`${parseFloat(a.sugar_value).toFixed(1)} mmol/L · `:""}{new Date(a.sent_at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
            </div>
          </div>
        )) : !stats?.trend && (
          <div className="d-alert ok">
            <div className="d-alert-icon">✅</div>
            <div><div className="d-alert-title">No alerts yet</div><div className="d-alert-body">Dangerous readings auto-alert contacts via WhatsApp.</div></div>
          </div>
        )}

        {/* Quick Status — mobile */}
        <div className="d-sh" style={{marginTop:4}}><span className="d-sh-title">Quick Status</span></div>
        <div className="d-card" style={{marginBottom:4,padding:0,overflow:"hidden"}}>
          {(()=>{
            const lastLog=logs[0], lastLogTime=lastLog?new Date(lastLog.logged_at||lastLog.created_at):null, now=new Date();
            function timeAgo(d){if(!d)return"—";const diff=now-d,m=Math.floor(diff/60000),h=Math.floor(diff/3600000),dy=Math.floor(diff/86400000);return m<1?"Now":m<60?`${m}m ago`:h<24?`${h}h ago`:`${dy}d ago`;}
            function nextCheckVal(d){if(!d)return"—";const nx=new Date(d.getTime()+2*3600000);if(nx<now)return"Now";const diff=nx-now,m=Math.floor(diff/60000),h=Math.floor(diff/3600000);return m<60?`${m} min`:`${h}h ${String(Math.floor((diff%3600000)/60000)).padStart(2,"0")}m`;}
            function nextMealVal(){const h=now.getHours();return h<8?"Breakfast":h<12?"Lunch":h<19?"Dinner":"Breakfast";}
            function nextMealTime(){const h=now.getHours();return h<8?"7:00 AM":h<12?"12:30 PM":h<19?"7:00 PM":"7:00 AM";}
            function nextMedTime(){const h=now.getHours();return h<7?"7:00 AM":h<13?"1:00 PM":h<19?"7:00 PM":"7:00 AM";}
            const isOverdue=lastLogTime&&(now-lastLogTime)>2*3600000;
            const tiles=[
              {lbl:"LAST INSULIN",val:timeAgo(lastLogTime),delta:lastLogTime?lastLogTime.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})+" · "+lastLogTime.toLocaleDateString("en-GB",{day:"numeric",month:"short"}):"No readings yet",c:"t"},
              {lbl:"NEXT CHECK",val:isOverdue?"Now":nextCheckVal(lastLogTime),delta:isOverdue?"Check immediately":lastLogTime?"Every 2h recommended":"Log first reading",c:isOverdue?"r":""},
              {lbl:"NEXT MEAL",val:nextMealVal(),delta:nextMealTime(),c:""},
              {lbl:"NEXT MEDICATION",val:nextMedTime(),delta:"Insulin",c:"b"},
            ];
            return <div className="d-g2" style={{marginBottom:0}}>{tiles.map((t,i)=>(
              <div className={`d-sc ${t.c}`} key={i} style={{padding:"11px 13px"}}>
                <div className="d-sc-lbl">{t.lbl}</div>
                <div className={`d-sc-val ${t.c}`} style={{fontSize:"clamp(13px,3.8vw,19px)",lineHeight:1.1}}>{t.val}</div>
                <div className="d-sc-delta up" style={{color:"var(--text2)",fontSize:"clamp(9px,2.5vw,11px)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.delta}</div>
              </div>
            ))}</div>;
          })()}
        </div>
      </div>

      {/* Blood sugar ring + quick actions */}
      <div className="d-g21">
        <div className="d-card">
          <div className="d-sh">
            <span className="d-sh-title">Blood Sugar Status</span>
            <span className={`d-chip d-chip-${latestStatus === "normal" ? "teal" : latestStatus === "high" ? "amber" : "red"}`}>
              {latestMmol != null
                ? (latestStatus === "normal" ? "Stable ✓" : latestStatus === "high" ? "Elevated ⚠" : "Low 🚨")
                : "No data yet"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div className="d-ring-wrap">
              <svg viewBox="0 0 130 130">
                <circle cx="65" cy="65" r="50" fill="none" stroke="var(--bg4)" strokeWidth="9" />
                <circle cx="65" cy="65" r="50" fill="none" stroke={ringColor} strokeWidth="9"
                  strokeLinecap="round" strokeDasharray="314.16" strokeDashoffset={ringOffset}
                  style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.34,1.56,.64,1)" }} />
              </svg>
              <div className="d-ring-center">
                <div className="d-ring-val" style={{ color: ringColor }}>
                  {latestMmol != null ? latestMmol.toFixed(1) : "—"}
                </div>
                <div className="d-ring-unit">mmol/L</div>
                <div className="d-ring-status" style={{ color: ringColor }}>
                  {latestMmol != null
                    ? (latestStatus === "normal" ? "Normal ✓" : latestStatus === "high" ? "High ⚠" : "Low 🚨")
                    : "Log a reading"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
              {[
                ["Latest",    latestMmol != null ? `${latestMmol.toFixed(1)} mmol/L` : "—"],
                ["30-Day avg", avgDisplay !== "—" ? `${avgDisplay} mmol/L` : "—"],
                ["In range",   inRange != null ? `${inRange}%` : "—"],
                ["HbA1c est.", stats?.hba1cEstimate ? `${stats.hba1cEstimate}%` : "—"],
              ].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: 9, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".4px" }}>{l}</div>
                  <div style={{ fontFamily: "Syne,sans-serif", fontSize: 16, fontWeight: 800 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <BarChart data={barData} colors={barColors} />
            <div className="d-chart-lbl">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(l => <span key={l}>{l}</span>)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* ── Quick Status — desktop only ── */}
          <div className="d-card d-desktop-only" style={{padding:"12px 14px"}}>
            {(()=>{
              const lastLog=logs[0],lastLogTime=lastLog?new Date(lastLog.logged_at||lastLog.created_at):null,now=new Date();
              function timeAgo(d){if(!d)return"—";const diff=now-d,m=Math.floor(diff/60000),h=Math.floor(diff/3600000),dy=Math.floor(diff/86400000);return m<1?"Now":m<60?`${m}m ago`:h<24?`${h}h ago`:`${dy}d ago`;}
              function nextCheckVal(d){if(!d)return"—";const nx=new Date(d.getTime()+2*3600000);if(nx<now)return"Now";const diff=nx-now,m=Math.floor(diff/60000),h=Math.floor(diff/3600000);return m<60?`${m} min`:`${h}h ${String(Math.floor((diff%3600000)/60000)).padStart(2,"0")}m`;}
              function nextMealVal(){const h=now.getHours();return h<8?"Breakfast":h<12?"Lunch":h<19?"Dinner":"Breakfast";}
              function nextMealTime(){const h=now.getHours();return h<8?"7:00 AM":h<12?"12:30 PM":h<19?"7:00 PM":"7:00 AM";}
              function nextMedTime(){const h=now.getHours();return h<7?"7:00 AM":h<13?"1:00 PM":h<19?"7:00 PM":"7:00 AM";}
              const isOverdue=lastLogTime&&(now-lastLogTime)>2*3600000;
              const tiles=[
                {lbl:"LAST INSULIN",val:timeAgo(lastLogTime),delta:lastLogTime?lastLogTime.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})+" · "+lastLogTime.toLocaleDateString("en-GB",{day:"numeric",month:"short"}):"No readings yet",c:"t"},
                {lbl:"NEXT CHECK",val:isOverdue?"Now":nextCheckVal(lastLogTime),delta:isOverdue?"Check immediately":lastLogTime?"Every 2h recommended":"Log first reading",c:isOverdue?"r":""},
                {lbl:"NEXT MEAL",val:nextMealVal(),delta:nextMealTime(),c:""},
                {lbl:"NEXT MEDICATION",val:nextMedTime(),delta:"Insulin",c:"b"},
              ];
              return (<div>
                <div style={{fontFamily:"Syne,sans-serif",fontSize:14,fontWeight:800,marginBottom:12}}>Quick Status</div>
                <div className="d-g2" style={{marginBottom:0}}>{tiles.map((t,i)=>(
                  <div className={`d-sc ${t.c}`} key={i} style={{padding:"11px 13px"}}>
                    <div className="d-sc-lbl">{t.lbl}</div>
                    <div className={`d-sc-val ${t.c}`} style={{fontSize:19,lineHeight:1.1}}>{t.val}</div>
                    <div className="d-sc-delta up" style={{color:"var(--text2)",fontSize:10,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.delta}</div>
                  </div>
                ))}</div>
              </div>);
            })()}
          </div>

          {/* Desktop only — hidden on mobile */}
          <div className="d-card-teal d-desktop-only" style={{ cursor: "pointer" }} onClick={() => onNav("nearby")}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📍</div>
            <div style={{ fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 3 }}>Find Food Near Me</div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>Live traffic · GI-ranked · {city}</div>
          </div>
          <div className="d-card-amber d-desktop-only" style={{ cursor: "pointer" }} onClick={() => onNav("meals")}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🍽️</div>
            <div style={{ fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 3 }}>Today's Chop Plan</div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>Regional · {curr.flag} {curr.name}</div>
          </div>
          <div className="d-card-blue d-desktop-only" style={{ cursor: "pointer" }} onClick={() => onNav("games")}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🎮</div>
            <div style={{ fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 3 }}>Daily Quiz</div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>Earn tokens · build knowledge</div>
          </div>

          {/* Mobile only — YouTube channel card */}
          <div className="d-mobile-only" onClick={() => onNav("education")} style={{
            cursor:"pointer", background:"linear-gradient(135deg,#1a0000 0%,#3d0000 40%,#1a0000 100%)",
            border:"1px solid rgba(255,82,82,.25)", borderRadius:"var(--r-md)",
            padding:0, overflow:"hidden", position:"relative",
          }}>
            <div style={{ position:"relative", paddingBottom:"56.25%", background:"#000" }}>
              <img src="https://img.youtube.com/vi/XuCnSoVtfWc/mqdefault.jpg" alt="The Diabetes Voice"
                style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:.85 }} />
              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(255,0,0,.9)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow:"0 4px 20px rgba(255,0,0,.5)" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><polygon points="5,3 19,12 5,21" /></svg>
                </div>
              </div>
            </div>
            <div style={{ padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <div style={{ background:"#ff0000", borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:800, color:"#fff" }}>▶ YOUTUBE</div>
                <div style={{ fontSize:10, color:"var(--text2)" }}>@TheDiabetesVoice</div>
              </div>
              <div style={{ fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:800, marginBottom:4 }}>The Diabetes Voice</div>
              <div style={{ fontSize:12, color:"var(--text2)", lineHeight:1.5 }}>
                Weekly episodes on diabetes · food safety · West Africa.
                <span style={{ color:"var(--teal)", fontWeight:600 }}> Tap to watch →</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts & Predictions — desktop only (mobile has it above) */}
      <div className="d-desktop-only">
        <div className="d-sh"><span className="d-sh-title">Alerts & Predictions</span></div>
        {stats?.trend && (
          <div className={`d-alert ${stats.trend === "rising" ? "warn" : stats.trend === "falling" && latestMmol < 5 ? "bad" : "ok"}`}>
            <div className="d-alert-icon">
              {stats.trend === "rising" ? "📈" : stats.trend === "falling" ? "📉" : "✅"}
            </div>
            <div>
              <div className="d-alert-title">
                {stats.trend === "rising" ? "Sugar Trending Upward"
                 : stats.trend === "falling" ? "Sugar Trending Downward"
                 : "Sugar Trend is Stable"}
              </div>
              <div className="d-alert-body">
                {stats.trend === "rising"
                  ? `Rising average: ${avgDisplay} mmol/L. Reduce carbs and take a 10-min walk.`
                  : stats.trend === "falling"
                  ? `Falling. ${latestMmol < 5 ? "Have a protein snack now." : "Keep monitoring."}`
                  : `Stable at ${avgDisplay} mmol/L. Your routine is working.`}
              </div>
            </div>
          </div>
        )}
        {alertHist.length > 0 ? alertHist.slice(0, 2).map((a, i) => (
          <div className={`d-alert ${a.alert_type === "low_sugar" ? "bad" : "warn"}`} key={i}>
            <div className="d-alert-icon">{a.alert_type === "low_sugar" ? "🚨" : "⚠️"}</div>
            <div>
              <div className="d-alert-title">
                {a.alert_type === "low_sugar" ? "🚨 Low Sugar — Family Alerted" : "⚠️ High Sugar — Family Alerted"}
              </div>
              <div className="d-alert-body">
                {a.sugar_value ? `${parseFloat(a.sugar_value).toFixed(1)} mmol/L · ` : ""}
                {new Date(a.sent_at).toLocaleString("en-GB", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}
              </div>
            </div>
          </div>
        )) : !stats?.trend && (
          <div className="d-alert ok">
            <div className="d-alert-icon">✅</div>
            <div>
              <div className="d-alert-title">No alerts sent yet</div>
              <div className="d-alert-body">Readings below 4 or above 13 mmol/L auto-alert contacts via WhatsApp.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRACKER PAGE — POST /api/sugar/log · GET /api/sugar/logs · GET /api/sugar/stats
// ═══════════════════════════════════════════════════════════════
function TrackerPage() {
  // User-specific localStorage key — prevents data leaking between users on same device
  const userId = api.auth.getUser()?.id || api.auth.getUser()?.email || "guest";
  const LS_KEY = `choptime_sugar_${userId}`;
  const [logs, setLogs]       = useState(() => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; } });
  const [val, setVal]         = useState("");
  const [timing, setTiming]   = useState("Before breakfast");
  const [unit, setUnit]       = useState("mmol/L");
  const [notes, setNotes]     = useState("");
  const [done, setDone]       = useState(false);
  const [saving, setSaving]   = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [syncing, setSyncing]           = useState(true);
  const [stats, setStats]               = useState(null);
  const [alertMsg, setAlertMsg]         = useState("");
  const [showSandboxTip, setShowSandboxTip] = useState(false);

  useEffect(() => {
    // Clear the old shared localStorage key to prevent cross-user data leaks
    localStorage.removeItem("choptime_sugar_logs");

    // GET /api/sugar/logs → { logs: [{ id, value, unit, timing, status, notes, logged_at }] }
    api.sugar.getLogs(30)
      .then(r => {
        const apiLogs = (r.logs || []).map(l => ({
          time:   new Date(l.logged_at).toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit" }),
          val:    parseFloat(l.value),
          meal:   l.timing,
          status: l.status, // already set by backend: 'low'|'normal'|'high'
          unit:   l.unit,
          notes:  l.notes || "",
          date:   new Date(l.logged_at).toLocaleDateString(),
          logged_at: l.logged_at,
        }));
        if (apiLogs.length > 0) {
          setLogs(apiLogs);
          localStorage.setItem(LS_KEY, JSON.stringify(apiLogs));
        }
      })
      .catch(() => {})
      .finally(() => setSyncing(false));

    // GET /api/sugar/stats → { stats: { average, minimum, maximum, timeInRange, hba1cEstimate, trend, totalReadings } }
    api.sugar.getStats()
      .then(r => { if (r.stats) setStats(r.stats); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(logs)); } catch {}
  }, [logs]);

  const col = s => s === "normal" ? "var(--teal)" : s === "high" ? "var(--amber)" : "var(--red)";

  async function log() {
    if (!val) return;
    const n = parseFloat(val);
    if (isNaN(n) || n < 0.5 || n > 60) return;
    const now = new Date();
    // Backend determines status — we optimistically set it here too
    let mmol = unit === "mg/dL" ? n / 18 : n;
    const status = mmol < 4 ? "low" : mmol > 7.8 ? "high" : "normal";
    const newLog = {
      time: now.toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit" }),
      val: n, meal: timing, status, unit, notes,
      date: now.toLocaleDateString(), logged_at: now.toISOString(),
    };
    // Optimistic UI update
    setLogs(prev => [newLog, ...prev].slice(0, 50));
    setVal(""); setNotes("");
    setDone(true);
    setTimeout(() => setDone(false), 3000);
    setSaving(true);
    try {
      if (!api.auth.isLoggedIn()) {
        throw new Error("Please sign in to save your readings to the cloud.");
      }
      // POST /api/sugar/log → { message, log, status, mmolValue, alertNeeded, alertType, advice }
      const r = await api.sugar.log(n, unit, timing, notes);

      // Send WhatsApp alert if reading is dangerous
      if (r.alertNeeded && r.alertType) {
        try {
          const alertRes = await api.alerts.send(r.alertType, r.mmolValue, "");
          const sent = alertRes.results?.filter(x => x.status === "sent" || x.status === "sent_sms") || [];
          if (sent.length > 0) {
            setAlertMsg(`🚨 WhatsApp alert sent to ${sent.length} emergency contact${sent.length > 1 ? "s" : ""}!`);
          } else if (alertRes.results?.length > 0) {
            // Sent but possibly Twilio sandbox issue
            setAlertMsg("⚠️ Alert attempted but contacts may need to join Twilio sandbox first. See below.");
            setShowSandboxTip(true);
          } else {
            setAlertMsg("⚠️ No emergency contacts found. Please add contacts in Profile.");
          }
        } catch (alertErr) {
          const alertMsg = alertErr.message || "";
          if (alertMsg.includes("No emergency contacts")) {
            setAlertMsg("⚠️ No emergency contacts added yet. Go to Profile → Emergency Contacts.");
          } else {
            setAlertMsg("⚠️ Alert failed: " + alertMsg);
          }
          setShowSandboxTip(true);
        }
        setTimeout(() => { setAlertMsg(""); setShowSandboxTip(false); }, 12000);
      }

      // Refresh stats after logging
      api.sugar.getStats().then(sr => { if (sr.stats) setStats(sr.stats); }).catch(() => {});
    } catch (e) {
      // Show error in the done button area
      const msg = e.message || "";
      if (msg.includes("sign in")) {
        setDone(false);
        alert("⚠️ " + msg + "\nThe reading is saved on this device but not to your account.");
      } else if (msg.includes("connect") || msg.includes("fetch")) {
        alert("⚠️ Server waking up. Reading saved locally. It will sync when server is ready.");
      }
      console.error("Log failed:", msg);
    } finally {
      setSaving(false);
    }
  }

  function clearLogs() {
    if (window.confirm("Clear all blood sugar logs? This cannot be undone.")) {
      setLogs([]);
      localStorage.removeItem(LS_KEY);
      setDeleted(true);
      setTimeout(() => setDeleted(false), 2000);
    }
  }

  const todayLogs = logs.filter(l => {
    try { return new Date(l.date || l.logged_at).toDateString() === new Date().toDateString(); }
    catch { return true; }
  });
  const todayAvg  = todayLogs.length ? (todayLogs.reduce((a, b) => a + b.val, 0) / todayLogs.length).toFixed(1) : "—";
  const todayLow  = todayLogs.length ? Math.min(...todayLogs.map(l => l.val)).toFixed(1) : "—";
  const todayHigh = todayLogs.length ? Math.max(...todayLogs.map(l => l.val)).toFixed(1) : "—";
  const barData   = logs.length >= 2 ? logs.slice(0, 7).map(l => l.val).reverse() : [5.4, 7.2, 6.1, 8.0, 5.9, 6.8, 6.2];
  const wc        = barData.map(v => v > 7.8 ? "d-br" : v > 7 ? "d-ba" : "d-bt");
  const days      = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  // Use backend stats if available, otherwise derive from local logs
  const timeInRange = stats?.timeInRange ?? (logs.length ? Math.round(logs.filter(l => l.val >= 4 && l.val <= 7.8).length / logs.length * 100) : 0);
  const avgDisplay  = stats?.average || todayAvg;
  const trendTip    = stats?.trend === "rising"
    ? "📈 Trending up. Reduce carbs at your next meal and take a 10-min walk."
    : stats?.trend === "falling"
    ? "📉 Trending down. Have a small protein snack — groundnuts or boiled egg."
    : "✅ Trend is stable. Keep up your current routine.";

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Blood Sugar Tracker</div>
          <div className="d-page-sub">
            {syncing ? "Loading your readings from cloud..." : `${logs.length} readings · synced to Supabase · AI prediction`}
          </div>
        </div>
        <div className="d-topbar-right">
          <button className="d-btn d-btn-ghost" style={{ fontSize: 11, color: "var(--red)" }} onClick={clearLogs}>
            {deleted ? "✓ Cleared" : "Clear logs"}
          </button>
        </div>
      </div>

      <div className="d-g21">
        <div>
          <div className="d-card" style={{ marginBottom: 14 }}>
            <div className="d-sh-title" style={{ marginBottom: 14 }}>Log New Reading</div>
            <div className="d-g2" style={{ marginBottom: 12 }}>
              <div>
                <div className="d-inp-lbl">Reading *</div>
                <input className="d-inp" type="number" step="0.1" placeholder="e.g. 6.2" value={val}
                  onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === "Enter" && log()} />
              </div>
              <div>
                <div className="d-inp-lbl">Unit</div>
                <select className="d-inp" value={unit} onChange={e => setUnit(e.target.value)}>
                  <option>mmol/L</option>
                  <option>mg/dL</option>
                </select>
              </div>
              <div>
                <div className="d-inp-lbl">When</div>
                <select className="d-inp" value={timing} onChange={e => setTiming(e.target.value)}>
                  <option>Before breakfast</option>
                  <option>After breakfast</option>
                  <option>Before lunch</option>
                  <option>After lunch</option>
                  <option>Before dinner</option>
                  <option>After dinner</option>
                  <option>Bedtime</option>
                  <option>Night</option>
                </select>
              </div>
              <div>
                <div className="d-inp-lbl">Notes</div>
                <input className="d-inp" placeholder="Optional" value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
            <button className="d-btn d-btn-teal" style={{ width: "100%" }} onClick={log} disabled={saving}>
              {done ? "✓ Saved to Cloud!" : saving ? "Saving to Supabase..." : "Record Reading"}
            </button>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8, textAlign: "center" }}>
              {saving ? "⏳ Syncing to Supabase..." : "☁️ Saved to your account · Readings below 4 or above 13 mmol/L auto-alert family"}
            </div>

            {/* Alert feedback */}
            {alertMsg && (
              <div style={{
                marginTop: 10, padding: "10px 14px", borderRadius: 10,
                background: alertMsg.startsWith("🚨") ? "rgba(0,212,160,.1)" : "rgba(255,171,46,.1)",
                border: `1px solid ${alertMsg.startsWith("🚨") ? "rgba(0,212,160,.3)" : "rgba(255,171,46,.3)"}`,
                fontSize: 13, lineHeight: 1.5,
                color: alertMsg.startsWith("🚨") ? "var(--teal)" : "var(--amber)",
                fontWeight: 600,
              }}>
                {alertMsg}
              </div>
            )}

            {/* Twilio sandbox tip */}
            {showSandboxTip && (
              <div style={{
                marginTop: 8, padding: "12px 14px", borderRadius: 10,
                background: "rgba(77,184,255,.08)", border: "1px solid rgba(77,184,255,.25)",
                fontSize: 12, lineHeight: 1.7, color: "var(--text2)",
              }}>
                <div style={{ fontWeight: 700, color: "var(--blue)", marginBottom: 4 }}>
                  📱 WhatsApp Sandbox Setup Required
                </div>
                <div>Your Twilio account is in <strong>sandbox mode</strong>. Each emergency contact must send this message to activate alerts:</div>
                <div style={{ background: "var(--bg2)", borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontFamily: "monospace", fontSize: 13, color: "var(--teal)", fontWeight: 700 }}>
                  join &lt;your-sandbox-word&gt;
                </div>
                <div>Send it to <strong style={{ color: "var(--teal)" }}>+1 (229) 814-0997</strong> on WhatsApp.</div>
                <div style={{ marginTop: 6 }}>Find your sandbox word at: <strong>console.twilio.com → Messaging → Try it out → Send a WhatsApp message</strong></div>
                <div style={{ marginTop: 6, color: "var(--amber)" }}>
                  💡 To remove this requirement permanently, upgrade Twilio and register a WhatsApp Business number.
                </div>
              </div>
            )}
          </div>
          <div className="d-card">
            <div className="d-sh">
              <span className="d-sh-title">Trend ({logs.length} readings)</span>
              <span className="d-chip d-chip-teal">Avg {avgDisplay}</span>
            </div>
            <BarChart data={barData} colors={wc} />
            <div className="d-chart-lbl">{days.map(l => <span key={l}>{l}</span>)}</div>
            <div className="d-helper" style={{ marginTop: 12 }}>{trendTip}</div>
          </div>
        </div>
        <div>
          <div className="d-card" style={{ marginBottom: 12 }}>
            <div className="d-sh">
              <span className="d-sh-title">All Logs</span>
              <span className="d-chip d-chip-teal">{logs.length} readings</span>
            </div>
            {syncing && (
              <div style={{ textAlign:"center", padding:"16px 0", fontSize:13, color:"var(--text3)" }}>
                Loading your readings from Supabase...
              </div>
            )}
            {!syncing && logs.length === 0 && (
              <div style={{ textAlign:"center", padding:"20px 0", fontSize:13, color:"var(--text3)" }}>
                No readings yet. Log your first reading above. ↑
              </div>
            )}
            {logs.slice(0, 8).map((l, i) => (
              <div className="d-log-row" key={i}>
                <div className="d-log-dot" style={{ background: col(l.status) }} />
                <div className="d-log-time">{l.time}</div>
                <div className="d-log-val" style={{ color: col(l.status) }}>{l.val} <span style={{fontSize:10,color:"var(--text3)"}}>{l.unit || "mmol/L"}</span></div>
                <div className="d-log-meal" style={{ flex: 1 }}>{l.meal}</div>
                {l.notes ? <div style={{fontSize:10,color:"var(--text3)",maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.notes}</div> : null}
                <span className="d-log-tag" style={{ background: l.status==="normal" ? "var(--teal-dim)" : l.status==="high" ? "var(--amber-dim)" : "var(--red-dim)", color: col(l.status) }}>
                  {l.status}
                </span>
              </div>
            ))}
            {logs.length > 8 && (
              <div style={{ fontSize:11, color:"var(--text3)", textAlign:"center", marginTop:8 }}>
                + {logs.length - 8} more readings saved in Supabase
              </div>
            )}
          </div>
          <div className="d-g2">
            {[
              { lbl:"Time in Range",  val: logs.length ? `${timeInRange}%` : "—", sub:"Target >70%",    c: timeInRange>=70 ? "t" : "r" },
              { lbl:"Today's Avg",    val: todayAvg,  sub:"mmol/L today", c:"" },
              { lbl:"Today's Low",    val: todayLow,  sub:"mmol/L",       c:"t" },
              { lbl:"Today's High",   val: todayHigh, sub:"mmol/L",       c:"a" },
            ].map((s,i) => (
              <div className={`d-sc ${s.c}`} key={i}>
                <div className="d-sc-lbl">{s.lbl}</div>
                <div className={`d-sc-val ${s.c}`} style={{ fontSize:20 }}>{s.val}</div>
                <div style={{ fontSize:11, color:"var(--text3)", marginTop:3 }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NEARBY PAGE — GET /api/nearby/food → { joints: [...], aiRecommendation }
// ═══════════════════════════════════════════════════════════════

// Low-GI food recommendations by country — shown in restaurant detail modal
// img: curated Unsplash photos of each dish (free, no API key needed)
const RESTAURANT_FOOD_RECS = {
  GH: [
    { name: "Grilled Tilapia", gi: 0,  carbs: 0,  emoji: "🐟",
      img: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=80",
      desc: "Best choice — zero carbs, high protein. Ask for extra okra stew on the side.", safe: true },
    { name: "Kenkey + Okra Stew", gi: 38, carbs: 30, emoji: "🌽",
      img: "https://images.unsplash.com/photo-1567306226416-28f0efdc88ce?w=400&q=80",
      desc: "1 small fist-sized kenkey with generous okra stew. Avoid extra pepper sauce.", safe: true },
    { name: "Beans Stew", gi: 42, carbs: 20, emoji: "🫘",
      img: "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=400&q=80",
      desc: "1 cup beans stew with garden eggs. Skip the white rice entirely.", safe: true },
    { name: "Boiled Unripe Plantain", gi: 45, carbs: 25, emoji: "🍌",
      img: "https://images.unsplash.com/photo-1574226516831-e1dff420e562?w=400&q=80",
      desc: "Unripe only — ripe plantain has much higher GI. 3 fingers max.", safe: true },
    { name: "Banku + Okro Soup", gi: 52, carbs: 35, emoji: "🍲",
      img: "https://images.unsplash.com/photo-1547592180-85f173990554?w=400&q=80",
      desc: "Moderate GI. Half portion only with generous okro soup and fish.", safe: true },
    { name: "White Rice", gi: 73, carbs: 45, emoji: "🍚",
      img: "https://images.unsplash.com/photo-1516684732162-798a0062be99?w=400&q=80",
      desc: "High GI — skip or take very small portion with plenty of protein stew.", safe: false },
  ],
  NG: [
    { name: "Moi Moi", gi: 35, carbs: 22, emoji: "🫔",
      img: "https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=400&q=80",
      desc: "Excellent — steamed bean pudding, low GI, very filling and nutritious.", safe: true },
    { name: "Bitter Leaf Soup", gi: 18, carbs: 8, emoji: "🥬",
      img: "https://images.unsplash.com/photo-1476718406336-bb5a9690ee2a?w=400&q=80",
      desc: "Very low GI. Order with small portion of wheat fufu — avoid eba.", safe: true },
    { name: "Egusi Soup", gi: 25, carbs: 12, emoji: "🍲",
      img: "https://images.unsplash.com/photo-1547592180-85f173990554?w=400&q=80",
      desc: "Good choice. Pair with small swallow portion — avoid eba (GI 87).", safe: true },
    { name: "Grilled Catfish", gi: 0, carbs: 0, emoji: "🐠",
      img: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=80",
      desc: "Zero carbs. Best protein option. Ask for pepper soup version.", safe: true },
    { name: "Eba (Garri)", gi: 87, carbs: 60, emoji: "⚠️",
      img: "https://images.unsplash.com/photo-1517093702195-d437e7a94c67?w=400&q=80",
      desc: "Very high GI — avoid entirely. Choose semovita or wheat fufu instead.", safe: false },
    { name: "Jollof Rice", gi: 70, carbs: 48, emoji: "🍛",
      img: "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=400&q=80",
      desc: "High GI. Very small portion only — no more than ¼ plate. Load up on protein.", safe: false },
  ],
  SN: [
    { name: "Yassa Poulet", gi: 30, carbs: 15, emoji: "🍗",
      img: "https://images.unsplash.com/photo-1598103442097-8b74394b95c3?w=400&q=80",
      desc: "Excellent — grilled chicken in lemon onion sauce. Very safe for diabetics.", safe: true },
    { name: "Niébé (Cowpea)", gi: 33, carbs: 18, emoji: "🫘",
      img: "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=400&q=80",
      desc: "Great protein and fibre. Ask for boiled version with light sauce.", safe: true },
    { name: "Grilled Thiof", gi: 0, carbs: 0, emoji: "🐟",
      img: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=80",
      desc: "Zero GI. The best choice at any Senegalese restaurant.", safe: true },
    { name: "Millet Couscous", gi: 44, carbs: 28, emoji: "🌾",
      img: "https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?w=400&q=80",
      desc: "Moderate GI. Half cup portion only with plenty of sauce.", safe: true },
    { name: "Thiéboudienne", gi: 65, carbs: 52, emoji: "⚠️",
      img: "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=400&q=80",
      desc: "High GI from the rice. Very small rice portion — focus on the fish.", safe: false },
  ],
  CI: [
    { name: "Kedjenou Chicken", gi: 22, carbs: 10, emoji: "🍗",
      img: "https://images.unsplash.com/photo-1598103442097-8b74394b95c3?w=400&q=80",
      desc: "Excellent — slow-cooked chicken in vegetable broth. Very low GI.", safe: true },
    { name: "Grilled Fish", gi: 0, carbs: 0, emoji: "🐟",
      img: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=80",
      desc: "Best option at any maquis. Ask for grilled, not fried.", safe: true },
    { name: "Attiéké + Poisson", gi: 40, carbs: 30, emoji: "🐠",
      img: "https://images.unsplash.com/photo-1574226516831-e1dff420e562?w=400&q=80",
      desc: "Moderate. Small attiéké portion, large fish portion. Good balance.", safe: true },
    { name: "Aloko (Fried Plantain)", gi: 65, carbs: 38, emoji: "⚠️",
      img: "https://images.unsplash.com/photo-1574226516831-e1dff420e562?w=400&q=80",
      desc: "High GI from frying. Avoid or take 2-3 pieces only as a side.", safe: false },
  ],
  CM: [
    { name: "Ndolé", gi: 15, carbs: 6, emoji: "🥬",
      img: "https://images.unsplash.com/photo-1476718406336-bb5a9690ee2a?w=400&q=80",
      desc: "One of the best choices in West Africa — bitter leaf stew, extremely low GI.", safe: true },
    { name: "Grilled Fish", gi: 0, carbs: 0, emoji: "🐟",
      img: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=80",
      desc: "Zero carbs. Ask for grilled tilapia or catfish with light pepper.", safe: true },
    { name: "Eru Soup", gi: 12, carbs: 5, emoji: "🌿",
      img: "https://images.unsplash.com/photo-1547592180-85f173990554?w=400&q=80",
      desc: "Excellent — waterleaf and oil bean stew, extremely low GI.", safe: true },
    { name: "Boiled Plantain", gi: 45, carbs: 25, emoji: "🍌",
      img: "https://images.unsplash.com/photo-1574226516831-e1dff420e562?w=400&q=80",
      desc: "Unripe only. 3 fingers max paired with ndolé or eru soup.", safe: true },
  ],
};

function NearbyPage({ country, city, onCityChange }) {
  const curr   = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];
  // Demo joints as fallback (from original data)
  const demoJoints = FOOD_JOINTS[city] || FOOD_JOINTS["Accra"];

  const [joints, setJoints]       = useState([]);
  const [aiRec, setAiRec]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [searched, setSearched]   = useState(false);
  const [locErr, setLocErr]       = useState("");
  const [userLoc, setUserLoc]     = useState(null);
  const [radius, setRadius]       = useState(3000);
  const [selected, setSelected]   = useState(null); // selected restaurant for detail modal

  // Auto-search when component mounts if logged in
  useEffect(() => {
    if (api.auth.isLoggedIn()) {
      handleSearch();
    }
  }, [country]); // re-search when country changes

  async function handleSearch() {
    // Must be logged in
    if (!api.auth.isLoggedIn()) {
      setLocErr("Please sign in to find food near you. Your account is needed to personalise results for your country.");
      return;
    }

    setLoading(true);
    setLocErr("");
    try {
      const loc = await api.nearby.getCurrentLocation();
      setUserLoc(loc);
      const r = await api.nearby.getFood(loc.lat, loc.lng, country, radius);
      setJoints(r.joints || []);
      setAiRec(r.aiRecommendation || "");
      setSearched(true);
    } catch (e) {
      const msg = e.message || "";
      if (msg.includes("Location") || msg.includes("denied") || msg.includes("geolocation")) {
        setLocErr("📍 Location access denied. Please allow location access in your browser settings, then tap Search again.");
      } else if (msg.includes("Not logged in") || msg.includes("session")) {
        setLocErr("⚠️ Session expired. Please refresh and sign in again.");
      } else if (msg.includes("connect") || msg.includes("fetch")) {
        setLocErr("⚠️ Server is waking up. Please wait 30 seconds and tap Search again.");
      } else {
        setLocErr(msg || "Could not find nearby food. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Nearby Food & Traffic</div>
          <div className="d-page-sub">Real Google Places · Live traffic · {curr.flag} {curr.name}</div>
        </div>
        <div className="d-topbar-right">
          <span className="d-chip d-chip-teal">📡 Live</span>
        </div>
      </div>

      {/* Search controls */}
      <div className="d-card" style={{ marginBottom: 14 }}>
        <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:150 }}>
            <div className="d-inp-lbl">Search Radius</div>
            <select className="d-inp" value={radius} onChange={e => setRadius(+e.target.value)}>
              <option value={500}>500m (walking)</option>
              <option value={1000}>1 km</option>
              <option value={2000}>2 km</option>
              <option value={3000}>3 km</option>
              <option value={5000}>5 km</option>
            </select>
          </div>
          <button className="d-btn d-btn-teal" style={{ padding:"10px 20px" }} onClick={handleSearch} disabled={loading}>
            {loading ? "📡 Locating…" : "📍 Find Real Nearby Food"}
          </button>
        </div>
        {locErr && <div style={{ fontSize:12, color:"var(--red)", marginTop:8 }}>⚠️ {locErr}</div>}
        {userLoc && <div style={{ fontSize:11, color:"var(--teal)", marginTop:6 }}>✅ Location: {userLoc.lat.toFixed(4)}, {userLoc.lng.toFixed(4)}</div>}
      </div>

      {/* Real results from Google Places */}
      {searched && joints.length > 0 && (
        <div className="d-map-panel" style={{ marginBottom:14 }}>
          <div className="d-map-header">
            <div>
              <div className="d-map-title">🗺️ {joints.length} Restaurants Near You · Google Places</div>
              <div style={{ fontSize:11, color:"var(--text2)", marginTop:2 }}>Ranked by proximity · live traffic · GI advice</div>
            </div>
            <div className="d-map-live"><div className="d-map-live-dot" />Live</div>
          </div>
          <div className="d-map-body">
            {aiRec && (
              <div className="d-helper">
                🤖 <strong style={{ color:"var(--teal3)" }}>AI Recommends:</strong> {aiRec}
              </div>
            )}
            {joints.map((j, i) => (
              <div key={i} className={`d-joint-card ${j.recommended ? "best" : ""}`}
                onClick={() => setSelected(j)}
                style={{ cursor: "pointer" }}
              >
                {j.recommended && <div className="d-best-badge">AI Pick ★</div>}
                {/* Restaurant thumbnail — real Google Places photo or emoji fallback */}
                <div style={{ width:52, height:52, borderRadius:10, overflow:"hidden", background:"var(--bg3)", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {j.photoUrl ? (
                    <img src={j.photoUrl} alt={j.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}
                      onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
                    />
                  ) : null}
                  <div style={{ display: j.photoUrl ? "none" : "flex", alignItems:"center", justifyContent:"center", fontSize:24, width:"100%", height:"100%" }}>🍽️</div>
                </div>
                <div style={{ flex:1 }}>
                  <div className="d-joint-name">{j.name}</div>
                  <div className="d-joint-food">{j.address}</div>
                  <div className="d-joint-meta">
                    {j.rating && <span className="d-joint-dist">⭐ {j.rating} ({j.reviewCount || "?"})</span>}
                    {j.distance && <span className="d-joint-dist">📍 {j.distance}km</span>}
                    {j.travelTime && <span className="d-joint-time">🕐 {j.travelTime} min</span>}
                    {j.trafficLevel && j.trafficLevel !== "unknown" && <TrafficPill level={j.trafficLevel} />}
                    {j.isOpen != null && (
                      <span style={{ fontSize:11, color: j.isOpen ? "var(--teal)" : "var(--red)" }}>
                        {j.isOpen ? "● Open now" : "● Closed"}
                      </span>
                    )}
                  </div>
                  {j.trafficNote && (
                    <div style={{ fontSize:11, color: j.trafficLevel==="heavy" ? "var(--red)" : j.trafficLevel==="moderate" ? "var(--amber)" : "var(--text2)", marginTop:5, lineHeight:1.4 }}>
                      {j.trafficNote}
                    </div>
                  )}
                  {j.giNote && (
                    <div style={{ fontSize:11, color:"var(--teal)", marginTop:3 }}>🥗 {j.giNote}</div>
                  )}
                </div>
                <div style={{ fontSize:11, color:"var(--text3)", flexShrink:0 }}>Tap →</div>
              </div>
            ))}

            {/* Restaurant Detail Modal */}
            {selected && (
              <div style={{
                position:"fixed", inset:0, background:"rgba(0,0,0,.75)",
                zIndex:500, display:"flex", alignItems:"flex-end",
                animation:"d-fadeIn .2s ease",
              }} onClick={() => setSelected(null)}>
                <div style={{
                  background:"var(--bg1)", borderRadius:"20px 20px 0 0",
                  width:"100%", maxHeight:"85vh", overflowY:"auto",
                  padding:"0 0 40px", animation:"d-slideUp .25s ease",
                }} onClick={e => e.stopPropagation()}>

                  {/* Handle */}
                  <div style={{ display:"flex", justifyContent:"center", padding:"12px 0 4px" }}>
                    <div style={{ width:40, height:4, background:"var(--bg4)", borderRadius:2 }} />
                  </div>

                  {/* Restaurant photo from Google Places — URL built server-side */}
                  {(selected.photoUrl || selected.photoRef) && (
                    <div style={{ height:180, overflow:"hidden", background:"var(--bg3)", position:"relative" }}>
                      <img
                        src={selected.photoUrl || `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photo_reference=${selected.photoRef}`}
                        alt={selected.name}
                        style={{ width:"100%", height:"100%", objectFit:"cover" }}
                        onError={e => { e.target.parentElement.style.display="none"; }}
                      />
                      <div style={{
                        position:"absolute", bottom:0, left:0, right:0,
                        background:"linear-gradient(transparent, rgba(6,9,11,.8))",
                        padding:"20px 16px 12px",
                        fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:900, color:"#fff",
                      }}>
                        {selected.name}
                      </div>
                    </div>
                  )}

                  {/* Header */}
                  <div style={{ padding:"12px 20px 16px", borderBottom:"1px solid var(--border)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontFamily:"Syne,sans-serif", fontSize:20, fontWeight:900, marginBottom:4 }}>
                          {selected.name}
                        </div>
                        <div style={{ fontSize:13, color:"var(--text2)", marginBottom:6 }}>{selected.address}</div>
                        <div style={{ display:"flex", gap:12, flexWrap:"wrap", fontSize:12 }}>
                          {selected.rating && <span>⭐ {selected.rating} ({selected.reviewCount} reviews)</span>}
                          <span>📍 {selected.distance}km away</span>
                          <span>🕐 ~{selected.travelTime} min</span>
                          {selected.isOpen != null && (
                            <span style={{ color: selected.isOpen ? "var(--teal)" : "var(--red)", fontWeight:700 }}>
                              {selected.isOpen ? "● Open Now" : "● Closed"}
                            </span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => setSelected(null)} style={{
                        background:"var(--bg3)", border:"none", borderRadius:"50%",
                        width:32, height:32, cursor:"pointer", color:"var(--text2)",
                        fontSize:16, display:"flex", alignItems:"center", justifyContent:"center",
                        flexShrink:0,
                      }}>✕</button>
                    </div>

                    {/* Traffic */}
                    {selected.trafficNote && (
                      <div style={{
                        marginTop:10, padding:"8px 12px", borderRadius:8, fontSize:12,
                        background: selected.trafficLevel==="heavy" ? "rgba(255,82,82,.1)" : selected.trafficLevel==="moderate" ? "rgba(255,171,46,.1)" : "rgba(0,212,160,.1)",
                        color: selected.trafficLevel==="heavy" ? "var(--red)" : selected.trafficLevel==="moderate" ? "var(--amber)" : "var(--teal)",
                        border: `1px solid ${selected.trafficLevel==="heavy" ? "rgba(255,82,82,.2)" : selected.trafficLevel==="moderate" ? "rgba(255,171,46,.2)" : "rgba(0,212,160,.2)"}`,
                      }}>
                        {selected.trafficNote}
                      </div>
                    )}

                    {/* Google Maps link */}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.name + " " + selected.address)}&query_place_id=${selected.id || ""}`}
                      target="_blank" rel="noreferrer"
                      className="d-btn d-btn-teal"
                      style={{ display:"inline-flex", marginTop:12, textDecoration:"none", fontSize:13, padding:"8px 16px" }}
                    >
                      🗺️ Open in Google Maps
                    </a>
                  </div>

                  {/* What to eat — ChopTime recommended foods */}
                  <div style={{ padding:"16px 20px" }}>
                    <div style={{ fontFamily:"Syne,sans-serif", fontSize:16, fontWeight:800, marginBottom:4 }}>
                      🥗 What to Order Here
                    </div>
                    <div style={{ fontSize:12, color:"var(--text2)", marginBottom:14 }}>
                      ChopTime AI recommendations for diabetics at this restaurant
                    </div>

                    {/* Safe choices first */}
                    <div style={{ fontSize:11, color:"var(--teal)", fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", marginBottom:8 }}>
                      ✅ Safe Choices (Low GI)
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                      {(RESTAURANT_FOOD_RECS[country] || RESTAURANT_FOOD_RECS.GH)
                        .filter(f => f.safe)
                        .map((food, fi) => (
                        <div key={fi} style={{
                          background:"var(--bg2)", border:"1px solid rgba(0,212,160,.2)",
                          borderRadius:12, overflow:"hidden",
                        }}>
                          {/* Real food photo */}
                          <div style={{ position:"relative", height:110, overflow:"hidden", background:"var(--bg3)" }}>
                            <img
                              src={food.img}
                              alt={food.name}
                              style={{ width:"100%", height:"100%", objectFit:"cover" }}
                              onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
                            />
                            {/* Emoji fallback if image fails */}
                            <div style={{ display:"none", position:"absolute", inset:0, alignItems:"center", justifyContent:"center", fontSize:40 }}>
                              {food.emoji}
                            </div>
                            {/* GI badge overlay */}
                            <div style={{
                              position:"absolute", top:8, right:8,
                              fontSize:11, fontWeight:800, padding:"3px 8px", borderRadius:99,
                              background: food.gi === 0 ? "rgba(77,184,255,.9)" : food.gi <= 40 ? "rgba(0,212,160,.9)" : "rgba(255,171,46,.9)",
                              color: "#06090b",
                            }}>
                              {food.gi === 0 ? "Zero GI" : `GI ${food.gi}`}
                            </div>
                          </div>
                          <div style={{ padding:"10px 12px" }}>
                            <div style={{ fontWeight:700, fontSize:13, marginBottom:3 }}>{food.name}</div>
                            <div style={{ fontSize:11, color:"var(--text2)", marginBottom:5 }}>
                              {food.carbs === 0 ? "No carbs" : `${food.carbs}g carbs`}
                            </div>
                            <div style={{ fontSize:11, color:"var(--text2)", lineHeight:1.5 }}>{food.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Avoid section */}
                    <div style={{ fontSize:11, color:"var(--red)", fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", marginBottom:8 }}>
                      ⚠️ Avoid or Limit
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {(RESTAURANT_FOOD_RECS[country] || RESTAURANT_FOOD_RECS.GH)
                        .filter(f => !f.safe)
                        .map((food, fi) => (
                        <div key={fi} style={{
                          background:"rgba(255,82,82,.06)", border:"1px solid rgba(255,82,82,.15)",
                          borderRadius:10, overflow:"hidden", display:"flex", gap:0,
                        }}>
                          {/* Small food image on left */}
                          <div style={{ width:80, flexShrink:0, position:"relative", background:"var(--bg3)" }}>
                            <img
                              src={food.img}
                              alt={food.name}
                              style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                              onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
                            />
                            <div style={{ display:"none", position:"absolute", inset:0, alignItems:"center", justifyContent:"center", fontSize:28 }}>
                              {food.emoji}
                            </div>
                          </div>
                          <div style={{ padding:"10px 14px", flex:1 }}>
                            <div style={{ fontWeight:700, fontSize:14, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                              {food.name}
                              <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:99, background:"rgba(255,82,82,.15)", color:"var(--red)" }}>
                                GI {food.gi}
                              </span>
                            </div>
                            <div style={{ fontSize:12, color:"var(--text2)", marginTop:4, lineHeight:1.5 }}>{food.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* General tip */}
                    <div style={{
                      marginTop:16, padding:"12px 14px", borderRadius:10,
                      background:"rgba(0,212,160,.06)", border:"1px solid rgba(0,212,160,.15)",
                      fontSize:13, color:"var(--text2)", lineHeight:1.6,
                    }}>
                      💡 <strong style={{ color:"var(--teal)" }}>ChopTime Tip:</strong> Ask for your soup or stew to be served separately so you control how much carb you eat with it. Drink water before your meal to slow sugar absorption.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {searched && joints.length === 0 && (
        <div className="d-card" style={{ textAlign:"center", padding:32, marginBottom:14, color:"var(--text2)" }}>
          No restaurants found within {(radius/1000).toFixed(1)}km. Try increasing the radius.
        </div>
      )}

      {/* Demo data while not yet searched */}
      {!searched && (
        <div className="d-map-panel">
          <div className="d-map-header">
            <div>
              <div className="d-map-title">🗺️ Sample Nearby Food Joints</div>
              <div style={{ fontSize:11, color:"var(--text2)", marginTop:2 }}>Click "Find Real Nearby Food" for live Google Places data</div>
            </div>
            <div className="d-map-live"><div className="d-map-live-dot" />Demo</div>
          </div>
          <div className="d-map-body">
            <div className="d-city-tabs">
              {curr.cities.map(c => (
                <div key={c} className={`d-city-tab ${city===c ? "active" : ""}`} onClick={() => onCityChange(c)}>{c}</div>
              ))}
            </div>
            <div className="d-helper">
              🤖 <strong style={{ color:"var(--teal3)" }}>AI Recommends:</strong>{" "}
              Go to <strong style={{ color:"var(--teal)" }}>{demoJoints.find(j => j.best)?.name}</strong>{" "}
              — closest + lowest GI + clear traffic.
            </div>
            {demoJoints.map((j, i) => (
              <div className={`d-joint-card ${j.best ? "best" : ""}`} key={i}>
                {j.best && <div className="d-best-badge">AI Pick ★</div>}
                <div className="d-joint-emoji">{j.emoji}</div>
                <div style={{ flex:1 }}>
                  <div className="d-joint-name">{j.name}</div>
                  <div className="d-joint-food">{j.food}</div>
                  <div className="d-joint-meta">
                    <span className="d-joint-dist">📍 {j.dist}</span>
                    <span className="d-joint-time">🕐 {j.mins}</span>
                    <TrafficPill level={j.traffic} />
                  </div>
                  <div style={{ fontSize:11, color: j.traffic==="heavy" ? "var(--red)" : j.traffic==="moderate" ? "var(--amber)" : "var(--text2)", marginTop:5 }}>
                    {j.note}
                  </div>
                </div>
                <div className="d-food-right">
                  <GiChip gi={j.gi} />
                  <div style={{ fontSize:10, color:"var(--text3)", marginTop:4 }}>{j.area}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="d-card-red">
        <div style={{ fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:800, marginBottom:8 }}>⚠️ The Traffic-Diabetes Trap</div>
        <div style={{ fontSize:13, color:"var(--text2)", lineHeight:1.65 }}>
          In Lagos, Accra & Dakar, heavy traffic forces meal delays of 1–3 hours. Blood sugar{" "}
          <strong style={{ color:"var(--red)" }}>drops then spikes sharply</strong>{" "}
          when you finally eat. ChopTime finds the closest safe food spot <strong>before</strong> you get stuck.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMMUNITY PAGE — static community posts + real WhatsApp sharing
// ═══════════════════════════════════════════════════════════════
function CommunityPage() {
  const posts = [
    { initials:"AK", name:"Akosua K.", flag:"🇬🇭", city:"Kumasi", time:"2h ago",
      body:"My fasting sugar dropped to 5.1 after 2 weeks on the kenkey + okra plan — down from 8.4! The nearby food finder helped me find a chop bar 400m away I never knew existed. 🙏",
      likes:38, replies:14, color:"#0d6e3a" },
    { initials:"CJ", name:"Chidi J.", flag:"🇳🇬", city:"Lagos", time:"4h ago",
      body:"The traffic alert saved me yesterday — it told me not to go to VI because of Third Mainland gridlock. My sugar would have spiked waiting in traffic with no food. This app understands Lagos life!",
      likes:52, replies:21, color:"#6e2e0d" },
    { initials:"FD", name:"Fatou D.", flag:"🇸🇳", city:"Dakar", time:"1d ago",
      body:"Je n'avais pas réalisé que le thiéboudienne standard avait un GI aussi élevé. L'app m'a aidée à mieux choisir. Mon médecin a remarqué l'amélioration ce mois-ci!",
      likes:29, replies:11, color:"#0d3a6e" },
  ];

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Community</div>
          <div className="d-page-sub">Ghana · Nigeria · Senegal · Côte d'Ivoire · Cameroon</div>
        </div>
        <a href="https://t.me/+eRJcCc7Adj44Yzg0" target="_blank" rel="noreferrer" className="d-btn d-btn-teal" style={{ textDecoration:"none" }}>
          + Join Group
        </a>
      </div>
      <div className="d-g4">
        {[
          { val:"1,247", lbl:"Members",           c:"t" },
          { val:"48",    lbl:"Online Now",         c:"" },
          { val:"5",     lbl:"Countries",          c:"" },
          { val:"94%",   lbl:"Questions Answered", c:"t" },
        ].map((s,i) => (
          <div className={`d-sc ${s.c}`} key={i}>
            <div className="d-sc-lbl">{s.lbl}</div>
            <div className={`d-sc-val ${s.c}`} style={{ fontSize:22 }}>{s.val}</div>
          </div>
        ))}
      </div>
      <div className="d-g21">
        <div>
          <div className="d-sh"><span className="d-sh-title">Community Feed</span></div>
          {posts.map((p, i) => (
            <div className="d-post" key={i}>
              <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:9 }}>
                <div className="d-post-av" style={{ background:p.color, color:"#fff" }}>{p.initials}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700 }}>
                    {p.name} {p.flag}
                    <span style={{ fontSize:10, background:"var(--teal-dim)", color:"var(--teal)", padding:"2px 7px", borderRadius:20, fontWeight:700, marginLeft:4 }}>✓</span>
                  </div>
                  <div style={{ fontSize:11, color:"var(--text3)" }}>{p.time} · {p.city}</div>
                </div>
              </div>
              <div className="d-post-body">{p.body}</div>
              <div style={{ display:"flex", gap:18 }}>
                <span className="d-post-act">❤️ {p.likes}</span>
                <span className="d-post-act">💬 {p.replies}</span>
                <span className="d-post-act">🔁 Share</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="d-sh"><span className="d-sh-title">Insulin Sharing Nearby</span></div>
          {[
            { name:"Ama Darko", city:"Accra, GH 🇬🇭", dist:"0.8 km", type:"Actrapid 4 units" },
            { name:"Emeka O.",  city:"Lagos, NG 🇳🇬", dist:"1.4 km", type:"NovoRapid 6 units" },
          ].map((s,i) => (
            <div key={i} style={{ background:"rgba(255,171,46,.05)", border:"1px solid rgba(255,171,46,.2)", borderRadius:"var(--r-md)", padding:14, marginBottom:9 }}>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <div style={{ fontSize:28 }}>💉</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"var(--amber)" }}>{s.name} · {s.dist}</div>
                  <div style={{ fontSize:12, color:"var(--text2)", marginTop:2 }}>{s.type} · {s.city}</div>
                  <div style={{ display:"flex", gap:7, marginTop:8 }}>
                    <a href="https://t.me/TheDiabetesVoice" target="_blank" rel="noreferrer" className="d-btn d-btn-ghost" style={{ fontSize:11, padding:"5px 11px", textDecoration:"none" }}>Message</a>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="d-divider" />
          <div className="d-sh"><span className="d-sh-title">Join Us</span></div>
          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            <a href="https://t.me/TheDiabetesVoice" target="_blank" rel="noreferrer" className="d-btn d-btn-teal" style={{ textDecoration:"none", justifyContent:"center" }}>📣 Telegram Channel →</a>
            <a href="https://t.me/+eRJcCc7Adj44Yzg0" target="_blank" rel="noreferrer" className="d-btn d-btn-ghost" style={{ textDecoration:"none", justifyContent:"center" }}>👥 Community Group →</a>
            <a href="https://bit.ly/40LkG3w" target="_blank" rel="noreferrer" className="d-btn d-btn-ghost" style={{ textDecoration:"none", justifyContent:"center" }}>💊 Free Insulin Pack →</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// GAMES PAGE — POST /api/quiz/submit · GET /api/quiz/stats
// ═══════════════════════════════════════════════════════════════
function GamesPage() {
  const [qi, setQi]           = useState(0);
  const [answered, setAnswered] = useState(false);
  const [chosen, setChosen]   = useState(null);
  const [saving, setSaving]   = useState(false);
  // Real token balance from GET /api/quiz/stats → { totalTokens, streakDays }
  const [tokens, setTokens]   = useState(0);
  const [streak, setStreak]   = useState(0);
  const q = QUIZ_QS[qi % QUIZ_QS.length];

  useEffect(() => {
    // GET /api/quiz/stats → { totalPlayed, correctAnswers, accuracy, totalTokens, streakDays }
    api.quiz.getStats()
      .then(r => {
        setTokens(r.totalTokens || 0);
        setStreak(r.streakDays || 0);
      })
      .catch(() => {});
  }, []);

  async function pick(opt, i) {
    if (answered || saving) return;
    setAnswered(true);
    setChosen(i);
    setSaving(true);
    try {
      // POST /api/quiz/submit → { correct, tokensEarned, totalTokens, streak, message }
      const r = await api.quiz.submit(q.q, opt.correct);
      setTokens(r.totalTokens || tokens + (opt.correct ? 20 : 0));
      setStreak(r.streak || streak);
    } catch {
      // optimistic update if API fails
      if (opt.correct) setTokens(t => t + 20);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Quiz & Rewards</div>
          <div className="d-page-sub">Earn tokens · redeem airtime · build knowledge</div>
        </div>
      </div>
      <div className="d-g21">
        <div>
          <div className="d-quiz-wrap">
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                <span style={{ fontSize:26 }}>🔥</span>
                <span style={{ fontFamily:"Syne,sans-serif", fontSize:30, fontWeight:900, color:"var(--amber)" }}>{streak}</span>
                <span style={{ fontSize:13, color:"var(--text2)" }}>day streak</span>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                  <span style={{ fontSize:18 }}>🪙</span>
                  <span style={{ fontFamily:"Syne,sans-serif", fontSize:24, fontWeight:900, color:"var(--amber)" }}>{tokens}</span>
                </div>
                <div style={{ fontSize:11, color:"var(--text2)" }}>tokens · saved to your account</div>
              </div>
            </div>
            <div className="d-quiz-q">{q.q}</div>
            <div className="d-quiz-opts">
              {q.opts.map((o, i) => (
                <div key={i}
                  className={`d-qopt${answered ? " d-qopt off" : ""}${answered && o.correct ? " correct" : ""}${answered && chosen===i && !o.correct ? " wrong" : ""}`}
                  onClick={() => pick(o, i)}
                >
                  <div style={{ fontWeight:700 }}>{o.label}</div>
                  <div style={{ fontSize:11, opacity:.7, marginTop:2 }}>{o.sub}</div>
                </div>
              ))}
            </div>
            {answered && (
              <div style={{ marginTop:14, background:"rgba(0,212,160,.08)", border:"1px solid rgba(0,212,160,.2)", borderRadius:10, padding:"12px 14px", animation:"d-fadeUp .3s ease" }}>
                <div style={{ fontSize:12, color: q.opts[chosen]?.correct ? "var(--teal)" : "var(--red)", fontWeight:700, marginBottom:6 }}>
                  {q.opts[chosen]?.correct
                    ? `✅ Correct! +20 tokens saved to your account`
                    : "❌ Not quite — but you're learning!"}
                  {saving && " ⏳"}
                </div>
                <div style={{ fontSize:12, color:"var(--text2)", lineHeight:1.65 }}>{q.explain}</div>
                <button className="d-btn d-btn-teal" style={{ marginTop:12 }} onClick={() => { setQi(q=>q+1); setAnswered(false); setChosen(null); }}>
                  Next Question →
                </button>
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="d-card" style={{ marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontSize:12, color:"var(--text2)" }}>Progress to Silver</span>
              <span style={{ fontSize:13, fontWeight:700, color:"var(--teal)" }}>{tokens}/500 🪙</span>
            </div>
            <div className="d-prog">
              <div className="d-prog-fill" style={{ width:`${Math.min((tokens/500)*100,100)}%` }} />
            </div>
          </div>
          {[
            { icon:"🥉", name:"Bronze Airtime",    pts:"200 tokens", sub:"₵2 / ₦500",   done: tokens>=200 },
            { icon:"🥈", name:"Silver Data Bundle", pts:"500 tokens", sub:"₵5 / ₦1,000", done: tokens>=500 },
            { icon:"🥇", name:"Gold Verified Badge",pts:"1,000 tokens",sub:"Free $5 badge",done: tokens>=1000 },
            { icon:"💎", name:"Premium Month",      pts:"2,000 tokens",sub:"30 days free", done: tokens>=2000 },
          ].map((t,i) => (
            <div className="d-tier" key={i}>
              <div className="d-tier-icon">{t.icon}</div>
              <div>
                <div className="d-tier-name">{t.name}</div>
                <div className="d-tier-pts">{t.pts} · {t.sub}</div>
              </div>
              <div style={{ marginLeft:"auto" }}>
                {t.done
                  ? <span className="d-chip d-chip-teal">Redeem</span>
                  : <span className="d-chip d-chip-gray">Locked</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROFILE PAGE — GET /api/auth/profile · PUT /api/auth/profile
//                GET /api/contacts · POST /api/contacts · DELETE /api/contacts/:id
// ═══════════════════════════════════════════════════════════════
function ProfilePage({ country, onLogout }) {
  const curr = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];

  // profile fields: first_name, last_name, email, phone, country, city, diabetes_type,
  //                 target_low, target_high, insulin_type, daily_dose, tokens, streak_days, is_premium
  const [profile, setProfile]     = useState(api.auth.getUser());
  // contacts fields: id, name, phone, whatsapp, relationship, is_primary
  const [contacts, setContacts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(false);
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [newC, setNewC]           = useState({ name:"", phone:"", whatsapp:"", relationship:"Family", isPrimary:false });
  const [addingC, setAddingC]     = useState(false);
  const [msg, setMsg]             = useState("");

  useEffect(() => {
    Promise.allSettled([api.auth.getProfile(), api.contacts.getAll()])
      .then(([p, c]) => {
        if (p.status === "fulfilled") {
          // GET /api/auth/profile → { profile: { first_name, last_name, email, phone, country, city, diabetes_type, target_low, target_high, insulin_type, daily_dose, tokens, streak_days } }
          const u = p.value.profile;
          setProfile(u);
          setForm({
            firstName:    u.first_name || "",
            lastName:     u.last_name  || "",
            country:      u.country    || "GH",
            city:         u.city       || "Accra",
            diabetesType: u.diabetes_type || "",
            diagnosisYear:u.diagnosis_year || "",
            targetLow:    u.target_low  || 4.0,
            targetHigh:   u.target_high || 7.8,
            insulinType:  u.insulin_type || "",
            dailyDose:    u.daily_dose  || "",
            phone:        u.phone       || "",
          });
        }
        if (c.status === "fulfilled") {
          // GET /api/contacts → { contacts: [{ id, name, phone, whatsapp, relationship, is_primary }] }
          setContacts(c.value.contacts || []);
        }
        setLoading(false);
      });
  }, []);

  function flash(m) { setMsg(m); setTimeout(() => setMsg(""), 3500); }

  async function handleSave() {
    setSaving(true);
    try {
      // PUT /api/auth/profile → { message, profile }
      const r = await api.auth.updateProfile({
        firstName:    form.firstName,
        lastName:     form.lastName,
        country:      form.country,
        city:         form.city,
        diabetesType: form.diabetesType,
        diagnosisYear:form.diagnosisYear,
        targetLow:    form.targetLow,
        targetHigh:   form.targetHigh,
        insulinType:  form.insulinType,
        dailyDose:    form.dailyDose,
        phone:        form.phone,
      });
      if (r.profile) setProfile(r.profile);
      flash("✓ Profile saved!");
      setEditing(false);
    } catch(e) { flash("❌ " + e.message); }
    finally { setSaving(false); }
  }

  async function handleAddContact() {
    if (!newC.name || !newC.phone) { flash("⚠️ Name and phone are required"); return; }
    setAddingC(true);
    try {
      // POST /api/contacts → { message, contact: { id, name, phone, whatsapp, relationship, is_primary } }
      await api.contacts.add(newC.name, newC.phone, newC.whatsapp || newC.phone, newC.relationship, newC.isPrimary);
      flash("✓ Contact added! They will receive WhatsApp alerts.");
      setShowAdd(false);
      setNewC({ name:"", phone:"", whatsapp:"", relationship:"Family", isPrimary:false });
      const r = await api.contacts.getAll();
      setContacts(r.contacts || []);
    } catch(e) { flash("❌ " + e.message); }
    finally { setAddingC(false); }
  }

  async function handleRemoveContact(id) {
    if (!window.confirm("Remove this contact? They will no longer receive alerts.")) return;
    try {
      // DELETE /api/contacts/:id → { message }
      await api.contacts.remove(id);
      setContacts(cs => cs.filter(c => c.id !== id));
      flash("✓ Contact removed");
    } catch(e) { flash("❌ " + e.message); }
  }

  const u          = profile || {};
  const firstName  = u.first_name  || u.firstName  || "User";
  const lastName   = u.last_name   || u.lastName   || "";
  const email      = u.email       || "";
  const userTokens = u.tokens      || 0;
  const userStreak = u.streak_days || 0;

  if (loading) {
    return (
      <div className="d-page active">
        <div style={{ textAlign:"center", padding:60, color:"var(--text2)" }}>
          Loading your profile from Supabase...
        </div>
      </div>
    );
  }

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Profile</div>
          <div className="d-page-sub">Health data · emergency contacts · account</div>
        </div>
        {!editing && <button className="d-btn d-btn-ghost" onClick={() => setEditing(true)}>Edit Profile</button>}
      </div>

      {msg && (
        <div style={{ padding:"10px 14px", marginBottom:14, fontSize:13, borderRadius:10,
          background: msg.startsWith("✓") ? "var(--teal-dim)" : "var(--red-dim)",
          border: `1px solid ${msg.startsWith("✓") ? "rgba(0,212,160,.3)" : "rgba(255,82,82,.3)"}`,
          color: msg.startsWith("✓") ? "var(--teal)" : "var(--red)" }}>
          {msg}
        </div>
      )}

      <div className="d-g12">
        {/* Left col — avatar + quick links */}
        <div>
          <div className="d-card" style={{ textAlign:"center", padding:28, marginBottom:12 }}>
            <div style={{ width:76, height:76, borderRadius:"50%", background:"linear-gradient(135deg,var(--teal),var(--teal2))", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px", fontFamily:"Syne,sans-serif", fontWeight:900, fontSize:26, color:"var(--bg0)", boxShadow:"0 0 28px var(--teal-glow)" }}>
              {firstName.charAt(0)}{lastName.charAt(0) || ""}
            </div>
            <div style={{ fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:900 }}>{firstName} {lastName}</div>
            <div style={{ fontSize:12, color:"var(--text2)", margin:"5px 0 11px" }}>{email}</div>
            <div style={{ display:"flex", justifyContent:"center", gap:12, marginBottom:12 }}>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"Syne,sans-serif", fontSize:20, fontWeight:900, color:"var(--amber)" }}>{userTokens}</div>
                <div style={{ fontSize:10, color:"var(--text2)" }}>tokens</div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"Syne,sans-serif", fontSize:20, fontWeight:900, color:"var(--amber)" }}>🔥{userStreak}</div>
                <div style={{ fontSize:10, color:"var(--text2)" }}>day streak</div>
              </div>
            </div>
            <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"var(--teal-dim)", border:"1px solid rgba(0,212,160,.3)", borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:700, color:"var(--teal)" }}>
              ✓ Verified Member
            </div>
          </div>
          <div className="d-card" style={{ marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>Quick Links</div>
            {[
              { l:"💊 Insulin Pen Pack",  href:"https://bit.ly/40LkG3w" },
              { l:"▶ YouTube Channel",   href:"https://www.youtube.com/@TheDiabetesVoice" },
              { l:"📣 Telegram Channel", href:"https://t.me/TheDiabetesVoice" },
              { l:"👥 Telegram Group",   href:"https://t.me/+eRJcCc7Adj44Yzg0" },
            ].map(lk => (
              <div className="d-prof-row" key={lk.l}>
                <span className="d-prof-lbl">{lk.l}</span>
                <a href={lk.href} target="_blank" rel="noreferrer" style={{ fontSize:12, fontWeight:700, color:"var(--teal)", textDecoration:"none" }}>Open →</a>
              </div>
            ))}
          </div>
        </div>

        {/* Right col — health profile + emergency contacts */}
        <div>
          {editing ? (
            <div className="d-card" style={{ marginBottom:12 }}>
              <div style={{ fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:800, marginBottom:14 }}>Edit Health Profile</div>
              <div className="d-g2" style={{ marginBottom:12 }}>
                <div><div className="d-inp-lbl">First Name</div><input className="d-inp" value={form.firstName} onChange={e => setForm(f=>({...f,firstName:e.target.value}))} /></div>
                <div><div className="d-inp-lbl">Last Name</div><input className="d-inp" value={form.lastName} onChange={e => setForm(f=>({...f,lastName:e.target.value}))} /></div>
              </div>
              <div className="d-g2" style={{ marginBottom:12 }}>
                <div>
                  <div className="d-inp-lbl">Country</div>
                  <select className="d-inp" value={form.country} onChange={e => setForm(f=>({...f,country:e.target.value}))}>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div><div className="d-inp-lbl">City</div><input className="d-inp" value={form.city} onChange={e => setForm(f=>({...f,city:e.target.value}))} placeholder="e.g. Accra" /></div>
              </div>
              <div className="d-g2" style={{ marginBottom:12 }}>
                <div>
                  <div className="d-inp-lbl">Diabetes Type</div>
                  <select className="d-inp" value={form.diabetesType} onChange={e => setForm(f=>({...f,diabetesType:e.target.value}))}>
                    <option value="">Prefer not to say</option>
                    <option value="Type 1">Type 1</option>
                    <option value="Type 2">Type 2</option>
                    <option value="Gestational">Gestational</option>
                    <option value="Pre-diabetes">Pre-diabetes</option>
                  </select>
                </div>
                <div><div className="d-inp-lbl">Diagnosis Year</div><input className="d-inp" type="number" value={form.diagnosisYear} onChange={e => setForm(f=>({...f,diagnosisYear:e.target.value}))} placeholder="e.g. 2019" /></div>
              </div>
              <div className="d-g2" style={{ marginBottom:12 }}>
                <div><div className="d-inp-lbl">Target Low (mmol/L)</div><input className="d-inp" type="number" step="0.1" value={form.targetLow} onChange={e => setForm(f=>({...f,targetLow:e.target.value}))} /></div>
                <div><div className="d-inp-lbl">Target High (mmol/L)</div><input className="d-inp" type="number" step="0.1" value={form.targetHigh} onChange={e => setForm(f=>({...f,targetHigh:e.target.value}))} /></div>
              </div>
              <div className="d-g2" style={{ marginBottom:12 }}>
                <div><div className="d-inp-lbl">Insulin Type</div><input className="d-inp" value={form.insulinType} onChange={e => setForm(f=>({...f,insulinType:e.target.value}))} placeholder="e.g. Actrapid" /></div>
                <div><div className="d-inp-lbl">Daily Dose</div><input className="d-inp" value={form.dailyDose} onChange={e => setForm(f=>({...f,dailyDose:e.target.value}))} placeholder="e.g. 12u AM · 10u PM" /></div>
              </div>
              <div style={{ marginBottom:12 }}><div className="d-inp-lbl">Phone / WhatsApp</div><input className="d-inp" value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+233..." /></div>
              <div style={{ display:"flex", gap:10 }}>
                <button className="d-btn d-btn-teal" style={{ flex:1 }} onClick={handleSave} disabled={saving}>
                  {saving ? "Saving to Supabase..." : "Save Changes"}
                </button>
                <button className="d-btn d-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="d-card" style={{ marginBottom:12 }}>
              <div style={{ fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:800, marginBottom:12 }}>Health Profile</div>
              {[
                ["Diabetes Type",   u.diabetes_type   || "Not set"],
                ["Diagnosis Year",  u.diagnosis_year  || "Not set"],
                ["Target Range",    `${u.target_low||4.0} – ${u.target_high||7.8} mmol/L`],
                ["Insulin",         u.insulin_type    || "Not set"],
                ["Daily Dose",      u.daily_dose      || "Not set"],
                ["Country / City",  `${u.country||"GH"} · ${u.city||"Accra"}`],
                ["Phone / WhatsApp",u.phone           || "Not set"],
              ].map(([l, v]) => (
                <div className="d-prof-row" key={l}>
                  <span className="d-prof-lbl">{l}</span>
                  <span className="d-prof-val">{String(v)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Emergency contacts */}
          <div className="d-card" style={{ marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div>
                <div style={{ fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:800 }}>🚨 Emergency Contacts</div>
                <div style={{ fontSize:11, color:"var(--text2)" }}>Alerted via WhatsApp on dangerous readings · {contacts.length}/3 added</div>
              </div>
              {contacts.length < 3 && !showAdd && (
                <button className="d-btn d-btn-teal" style={{ fontSize:12, padding:"6px 12px" }} onClick={() => setShowAdd(true)}>+ Add Contact</button>
              )}
            </div>

            {showAdd && (
              <div style={{ background:"var(--bg2)", borderRadius:10, padding:14, marginBottom:14, border:"1px solid var(--border2)" }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>New Emergency Contact</div>
                <div className="d-g2" style={{ marginBottom:10 }}>
                  <div><div className="d-inp-lbl">Full Name *</div><input className="d-inp" placeholder="e.g. Akua Mensah" value={newC.name} onChange={e => setNewC(c=>({...c,name:e.target.value}))} /></div>
                  <div><div className="d-inp-lbl">Phone Number *</div><input className="d-inp" placeholder="+233..." value={newC.phone} onChange={e => setNewC(c=>({...c,phone:e.target.value}))} /></div>
                </div>
                <div className="d-g2" style={{ marginBottom:10 }}>
                  <div><div className="d-inp-lbl">WhatsApp (if different)</div><input className="d-inp" placeholder="Same as phone if blank" value={newC.whatsapp} onChange={e => setNewC(c=>({...c,whatsapp:e.target.value}))} /></div>
                  <div>
                    <div className="d-inp-lbl">Relationship</div>
                    <select className="d-inp" value={newC.relationship} onChange={e => setNewC(c=>({...c,relationship:e.target.value}))}>
                      {["Family","Spouse","Parent","Sibling","Child","Friend","Doctor"].map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="d-btn d-btn-teal" style={{ flex:1 }} onClick={handleAddContact} disabled={addingC}>
                    {addingC ? "Adding..." : "Add Contact"}
                  </button>
                  <button className="d-btn d-btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </div>
            )}

            {contacts.length === 0 && !showAdd ? (
              <div style={{ textAlign:"center", padding:"16px 0", fontSize:13, color:"var(--text3)" }}>
                No emergency contacts yet. Add a family member to receive WhatsApp alerts when your sugar is dangerous.
              </div>
            ) : contacts.map((c, i) => (
              <div key={c.id||i} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"10px 0", borderBottom:"1px solid var(--border)" }}>
                <div style={{ width:38, height:38, borderRadius:"50%", background:"var(--teal-dim)", border:"1px solid rgba(0,212,160,.3)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Syne,sans-serif", fontWeight:900, fontSize:14, color:"var(--teal)", flexShrink:0 }}>
                  {c.name.charAt(0)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>
                    {c.name}
                    {c.is_primary && <span className="d-chip d-chip-teal" style={{ marginLeft:8, fontSize:10 }}>Primary</span>}
                  </div>
                  <div style={{ fontSize:12, color:"var(--text2)" }}>{c.relationship} · {c.phone}</div>
                  {c.whatsapp && c.whatsapp !== c.phone && (
                    <div style={{ fontSize:11, color:"var(--teal)" }}>📱 WhatsApp: {c.whatsapp}</div>
                  )}
                </div>
                <button onClick={() => handleRemoveContact(c.id)}
                  style={{ background:"none", border:"none", color:"var(--text3)", cursor:"pointer", fontSize:18, padding:4, flexShrink:0 }}
                  title="Remove contact">🗑️</button>
              </div>
            ))}
          </div>

          {/* SaaS Metrics */}
          <div className="d-card" style={{ marginBottom:12 }}>
            <div style={{ fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:800, marginBottom:12 }}>SaaS Metrics</div>
            <div className="d-g2" style={{ marginBottom:0 }}>
              {[
                { lbl:"Monthly Active", val:"1.2k", c:"t" },
                { lbl:"Countries",     val:"5",    c:"" },
                { lbl:"30d Retention", val:"67%",  c:"t" },
                { lbl:"MRR",           val:"₵8.2k",c:"a" },
              ].map((s,i) => (
                <div className={`d-sc ${s.c}`} key={i} style={{ padding:"11px 13px" }}>
                  <div className="d-sc-lbl">{s.lbl}</div>
                  <div className={`d-sc-val ${s.c}`} style={{ fontSize:19 }}>{s.val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sign out */}
      <div className="d-logout-btn" onClick={onLogout}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        Sign Out · MGL2390 🕯️
      </div>
    </div>
  );
}
function MealsPage({ country }) {
  const [mealTab, setMealTab] = useState("b");
  const curr = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];
  const foods = FOOD_DB[country] || FOOD_DB.GH;

  const meals = {
    GH: {
      b: {
        name: "Oats Porridge + Boiled Egg",
        emoji: "🥣",
        gi: 55,
        carbs: 30,
        why: "Beta-glucan fiber slows glucose — full till 1 PM.",
        portion: "1 cup oats, 1 egg",
      },
      l: {
        name: "Ga Kenkey + Okra Stew + Grilled Tilapia",
        emoji: "🐟",
        gi: 41,
        carbs: 35,
        why: "Fermentation lowers kenkey GI. Okra slows absorption.",
        portion: "1 fist kenkey, generous okra, 1 fish",
      },
      d: {
        name: "Beans Stew + Unripe Plantain",
        emoji: "🫘",
        gi: 42,
        carbs: 28,
        why: "Resistant starch + fibre = stable overnight glucose.",
        portion: "1 cup beans, ½ plantain",
      },
      snacks: ["🥜 Groundnuts (GI 14)", "🥑 ½ Avocado (GI 15)"],
    },
    NG: {
      b: {
        name: "Moi Moi + Boiled Egg",
        emoji: "🟤",
        gi: 35,
        carbs: 22,
        why: "Steamed bean pudding — slow release, high protein.",
        portion: "1 medium wrap, 1 egg",
      },
      l: {
        name: "Bitter Leaf Soup + Grilled Catfish",
        emoji: "🐟",
        gi: 18,
        carbs: 8,
        why: "Extremely low GI — excellent diabetes meal.",
        portion: "1 bowl soup, 1 medium fish",
      },
      d: {
        name: "Ewa Agoyin + Small Plantain",
        emoji: "🫘",
        gi: 40,
        carbs: 25,
        why: "Beans GI 40 + unripe plantain resistant starch.",
        portion: "½ cup beans, ½ small plantain",
      },
      snacks: ["🥚 Boiled Egg", "🥜 Groundnuts (GI 14)"],
    },
    SN: {
      b: {
        name: "Millet Porridge + Groundnuts",
        emoji: "🌾",
        gi: 44,
        carbs: 28,
        why: "Millet lower GI than wheat, pairs with protein.",
        portion: "1 cup millet, small handful nuts",
      },
      l: {
        name: "Yassa Poulet + Niébé",
        emoji: "🍗",
        gi: 30,
        carbs: 22,
        why: "Lemon marinade + cowpeas = very low GI.",
        portion: "1 serving chicken, ½ cup cowpeas",
      },
      d: {
        name: "Grilled Thiof + Millet",
        emoji: "🐟",
        gi: 22,
        carbs: 18,
        why: "Fish is zero GI — millet lower GI than rice.",
        portion: "1 medium fish, ½ cup millet",
      },
      snacks: ["🥜 Groundnuts", "🫘 Boiled Niébé"],
    },
  };
  const dayMeals = meals[country] || meals.GH;
  const m = dayMeals[mealTab];

  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">Chop Planner</div>
          <div className="d-page-sub">
            Low-GI meals for {curr.name} — exact portions
          </div>
        </div>
      </div>
      <div className="d-g21">
        <div>
          <div className="d-mtabs">
            {[
              ["b", "🌅 Breakfast"],
              ["l", "☀️ Lunch"],
              ["d", "🌙 Dinner"],
            ].map(([k, l]) => (
              <div
                key={k}
                className={`d-mtab ${mealTab === k ? "active" : ""}`}
                onClick={() => setMealTab(k)}
              >
                {l}
              </div>
            ))}
          </div>
          <div className="d-card-teal" style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>{m.emoji}</div>
            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontSize: 17,
                fontWeight: 900,
                marginBottom: 8,
              }}
            >
              {m.name}
            </div>
            <div
              style={{
                display: "flex",
                gap: 7,
                marginBottom: 14,
                flexWrap: "wrap",
              }}
            >
              <GiChip gi={m.gi} />
              <span className="d-chip d-chip-amber">{m.carbs}g carbs</span>
              <span className="d-chip d-chip-teal">📏 {m.portion}</span>
            </div>
            <div
              style={{
                background: "rgba(0,212,160,.1)",
                border: "1px solid rgba(0,212,160,.2)",
                borderRadius: 10,
                padding: "10px 13px",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: "var(--teal)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                  marginBottom: 5,
                }}
              >
                Why this portion matters
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text2)",
                  lineHeight: 1.65,
                }}
              >
                {m.why}
              </div>
            </div>
          </div>
          <div className="d-card">
            <div className="d-sh-title" style={{ marginBottom: 10 }}>
              Today's Snacks
            </div>
            {dayMeals.snacks.map((s, i) => (
              <div
                key={i}
                style={{
                  padding: "8px 11px",
                  background: "var(--bg2)",
                  borderRadius: 8,
                  marginBottom: 5,
                  fontSize: 13,
                  color: "var(--text2)",
                }}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="d-sh">
            <span className="d-sh-title">Regional GI Guide</span>
          </div>
          <div className="d-card">
            {foods.map((f, i) => (
              <div className="d-food-row" key={i}>
                <div className="d-food-em">{f.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div className="d-food-nm">{f.name}</div>
                  <div className="d-food-why">{f.why}</div>
                </div>
                <div className="d-food-right">
                  <GiChip gi={f.gi} />
                  <div className="d-food-port">{f.portion}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EducationPage() {
  return (
    <div className="d-page active">
      <div className="d-topbar">
        <div>
          <div className="d-page-title">The Diabetes Voice</div>
          <div className="d-page-sub">
            3-minute weekly episodes · Dr. Richard LAAK · West Africa
          </div>
        </div>
        <div className="d-topbar-right">
          <button
            className="d-btn d-btn-ghost"
            onClick={() =>
              window.open("https://www.youtube.com/@TheDiabetesVoice", "_blank")
            }
          >
            ▶ YouTube
          </button>
          <button
            className="d-btn d-btn-teal"
            onClick={() =>
              window.open("https://t.me/TheDiabetesVoice", "_blank")
            }
          >
            Telegram
          </button>
        </div>
      </div>
      <div
        style={{
          background: "linear-gradient(140deg,#080818,#0e0e2a)",
          border: "1px solid rgba(77,184,255,.22)",
          borderRadius: "var(--r-xl)",
          padding: 26,
          marginBottom: 18,
          display: "flex",
          gap: 20,
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: 52, flexShrink: 0 }}>🌍</div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: "var(--blue)",
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              marginBottom: 7,
            }}
          >
            West Africa Edition · Featured
          </div>
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontSize: 18,
              fontWeight: 900,
              lineHeight: 1.3,
              marginBottom: 9,
            }}
          >
            Kenkey, Moi Moi, Attiéké: Which West African Staple is Safest for
            Diabetes?
          </div>
          <div
            style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14 }}
          >
            Episode 11 · 3 min · Dr. Richard LAAK · 7,400 views · 🇬🇭🇳🇬🇸🇳🇨🇮🇨🇲
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            <button className="d-btn d-btn-teal">▶ Watch Now</button>
            <button
              className="d-btn d-btn-ghost"
              onClick={() => window.open("https://bit.ly/40LkG3w", "_blank")}
            >
              📥 Free Insulin Pack
            </button>
          </div>
        </div>
      </div>
      <div className="d-g21">
        <div>
          <div className="d-sh">
            <span className="d-sh-title">All Episodes</span>
          </div>
          {EPISODES.map((ep, i) => (
            <div className="d-ep-card" key={i}>
              <div className="d-ep-thumb" style={{ background: ep.bg }}>
                {ep.emoji}
              </div>
              <div style={{ flex: 1 }}>
                <div className="d-ep-num">EPISODE {ep.num}</div>
                <div className="d-ep-title">{ep.title}</div>
                <div className="d-ep-meta">
                  <span>⏱ 3 min</span>
                  <span>👁 {ep.views} views</span>
                </div>
              </div>
              <div className="d-play-btn">
                <svg viewBox="0 0 10 12">
                  <path d="M0 0l10 6-10 6z" />
                </svg>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="d-card-teal" style={{ marginBottom: 12 }}>
            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontSize: 14,
                fontWeight: 800,
                marginBottom: 9,
              }}
            >
              📲 Join the Community
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--text2)",
                marginBottom: 13,
                lineHeight: 1.65,
              }}
            >
              1,247+ members across West Africa. New meal plans drop with every
              episode.
            </div>
            <button
              className="d-btn d-btn-teal"
              style={{ width: "100%", marginBottom: 8 }}
              onClick={() =>
                window.open("https://t.me/TheDiabetesVoice", "_blank")
              }
            >
              Telegram Channel →
            </button>
            <button
              className="d-btn d-btn-ghost"
              style={{ width: "100%" }}
              onClick={() =>
                window.open("https://t.me/+eRJcCc7Adj44Yzg0", "_blank")
              }
            >
              Telegram Group →
            </button>
          </div>
          <div className="d-card" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
              📥 Free Download
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--text2)",
                marginBottom: 12,
                lineHeight: 1.65,
              }}
            >
              Insulin Pen Optimization Pack — step-by-step guide for safer
              injection.
            </div>
            <button
              className="d-btn d-btn-teal"
              style={{ width: "100%" }}
              onClick={() => window.open("https://bit.ly/40LkG3w", "_blank")}
            >
              Download FREE →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const NAV_ITEMS = [
  {
    id: "home",
    label: "Dashboard",
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    id: "nearby",
    label: "Nearby 📍",
    badge: "Live",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    id: "tracker",
    label: "Blood Sugar",
    icon: (
      <svg viewBox="0 0 24 24">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    id: "meals",
    label: "Chop Planner",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
      </svg>
    ),
  },
  {
    id: "education",
    label: "Episodes",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <polygon points="10 8 16 12 10 16 10 8" />
      </svg>
    ),
  },
  {
    id: "community",
    label: "Community",
    badge: "48",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: "profile",
    label: "Profile",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
];

const MOBILE_NAV = [
  {
    id: "home",
    label: "Home",
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    id: "nearby",
    label: "Nearby",
    dot: true,
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    id: "tracker",
    label: "Sugar",
    icon: (
      <svg viewBox="0 0 24 24">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    id: "meals",
    label: "Chop",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
      </svg>
    ),
  },
  {
    id: "community",
    label: "Community",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: "profile",
    label: "Profile",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
];

/* ─── DASHBOARD ROOT ──────────────────────────────────────── */
// Map IP-detected country codes to our supported ones
const IP_COUNTRY_MAP = {
  GH: "GH", NG: "NG", SN: "SN", CI: "CI", CM: "CM",
  // fallbacks for neighbouring countries
  TG: "GH", BJ: "NG", GN: "GH", LR: "GH",
  NE: "NG", BF: "GH", ML: "SN", GM: "SN",
  GW: "SN", CV: "SN", MR: "SN",
  GA: "CM", CG: "CM", CF: "CM", TD: "CM",
};

export default function Dashboard({ onLogout }) {
  const [page, setPage] = useState("home");
  const [country, setCountry] = useState("GH");
  const [city, setCity] = useState("Accra");
  const [isDark, setIsDark] = useState(true);
  const [sheetOpen, setSheet] = useState(false);
  const [countryDetected, setCountryDetected] = useState(false);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.add("light");
    }
    return () => document.documentElement.classList.remove("light");
  }, [isDark]);

  // Auto-detect country on load
  useEffect(() => {
    async function detectCountry() {
      try {
        // 1. First check user's saved profile country (most accurate)
        if (api.auth.isLoggedIn()) {
          const profile = api.auth.getUser();
          if (profile?.country && COUNTRIES.find(c => c.code === profile.country)) {
            applyCountry(profile.country);
            setCountryDetected(true);
            return;
          }
          // Also try fresh from server
          api.auth.getProfile()
            .then(r => {
              const c = r.profile?.country;
              if (c && COUNTRIES.find(x => x.code === c)) {
                applyCountry(c);
                setCountryDetected(true);
              }
            })
            .catch(() => {});
        }

        // 2. Use IP geolocation as fallback
        const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(5000) });
        const data = await res.json();
        const detected = IP_COUNTRY_MAP[data.country_code];
        if (detected) {
          applyCountry(detected);
        }
        setCountryDetected(true);
      } catch {
        // Silent fail — default to GH
        setCountryDetected(true);
      }
    }
    detectCountry();
  }, []);

  function applyCountry(code) {
    setCountry(code);
    const c = COUNTRIES.find((x) => x.code === code);
    if (c) setCity(c.cities[0]);
  }

  function handleCountry(code) {
    applyCountry(code);
    setSheet(false);
  }

  const curr = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];
  const pageProps = { country, city, onNav: setPage, onCityChange: setCity };
  const sidebarUser = api.auth.getUser();

  const pages = {
    home: <HomePage {...pageProps} />,
    nearby: <NearbyPage {...pageProps} />,
    tracker: <TrackerPage />,
    meals: <MealsPage {...pageProps} />,
    education: <EducationPage />,
    community: <CommunityPage />,
    games: <GamesPage />,
    profile: <ProfilePage country={country} onLogout={onLogout} />,
  };

  const isLoggedIn = api.auth.isLoggedIn();

  return (
    <>
      <style>{CSS}</style>
      {/* Sign-in banner — shown when browsing without account */}
      {!isLoggedIn && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: "linear-gradient(90deg,#00d4a0,#009e78)",
          color: "#06090b", padding: "9px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600,
          boxShadow: "0 2px 16px rgba(0,212,160,.3)",
        }}>
          <span>🔑 Sign in to save readings, use AI, and find food near you.</span>
          <a href="/" style={{ color:"#06090b", fontWeight:800, textDecoration:"none",
            background:"rgba(0,0,0,.15)", padding:"4px 14px", borderRadius:20, whiteSpace:"nowrap" }}>
            Sign In →
          </a>
        </div>
      )}
      <div className="d-shell" style={!isLoggedIn ? { paddingTop: 38 } : {}}>
        {/* DESKTOP SIDEBAR */}
        <aside className="d-sidebar">
          <div className="d-logo-wrap">
            <div className="d-logo-row">
              <div className="d-logo-icon">🥘</div>
              <div>
                <div className="d-logo-name">
                  Chop<em>Time</em> AI
                </div>
                <div className="d-logo-tag">West Africa · Voice · Diabetes</div>
              </div>
            </div>
          </div>
          <div className="d-country-sel">
            <div className="d-country-lbl">Your Country</div>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 10px", background: "var(--bg3)",
              borderRadius: "var(--r-sm)", border: "1px solid var(--border)",
              fontSize: 13, fontWeight: 600, color: "var(--text)",
            }}>
              <span style={{ fontSize: 18 }}>{curr.flag}</span>
              <span>{curr.name}</span>
              <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--text3)" }}>
                ✓ set
              </span>
            </div>
            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4, paddingLeft: 2 }}>
              Update in Profile → Edit
            </div>
          </div>
          <div className="d-nav-wrap">
            {NAV_ITEMS.map((n) => (
              <div
                key={n.id}
                className={`d-nav-item ${page === n.id ? "active" : ""}`}
                onClick={() => setPage(n.id)}
              >
                <div className="d-ni-icon">{n.icon}</div>
                <span className="d-ni-label">{n.label}</span>
                {n.badge && <span className="d-ni-badge">{n.badge}</span>}
              </div>
            ))}
          </div>
          <div className="d-sidebar-foot">
            <div
              className="d-theme-toggle"
              onClick={() => setIsDark((d) => !d)}
            >
              <span style={{ fontSize: 14 }}>{isDark ? "🌙" : "☀️"}</span>
              <span className="d-toggle-lbl">
                {isDark ? "Dark mode" : "Light mode"}
              </span>
              <div className={`d-toggle-track ${isDark ? "on" : ""}`}>
                <div className="d-toggle-thumb">{isDark ? "🌙" : "☀️"}</div>
              </div>
            </div>
            <div className="d-user-row">
              <div className="d-user-av">{(sidebarUser?.first_name||sidebarUser?.firstName||"U").charAt(0)}</div>
              <div>
                <div className="d-user-name">{sidebarUser?.first_name || sidebarUser?.firstName || sidebarUser?.email?.split("@")[0] || "User"}</div>
                <div className="d-user-plan">✓ ChopTime AI · {sidebarUser?.country || "GH"}</div>
              </div>
            </div>
            <div className="d-logout-btn" onClick={onLogout}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Back to Landing Page
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="d-main">
          {/* mobile topbar */}
          <div className="d-mobile-topbar">
            <div className="d-mobile-logo">
              <div className="d-mobile-logo-icon">🥘</div>
              <div className="d-mobile-logo-name">
                Chop<em>Time</em> AI
              </div>
            </div>
            <div className="d-mobile-btns">
              <div
                className="d-mobile-theme-btn"
                onClick={() => setIsDark((d) => !d)}
              >
                {isDark ? "☀️" : "🌙"}
              </div>
              <div className="d-mobile-country-btn" style={{ cursor: "default", opacity: 0.85 }}>
                {curr.flag} {curr.name}
              </div>
            </div>
          </div>
          {pages[page]}
        </main>

        {/* MOBILE BOTTOM NAV */}
        <nav className="d-bottom-nav">
          <div className="d-bottom-nav-inner">
            {MOBILE_NAV.map((n) => (
              <div
                key={n.id}
                className={`d-bn-item ${page === n.id ? "active" : ""}`}
                onClick={() => setPage(n.id)}
              >
                {n.dot && page !== n.id && <div className="d-bn-dot" />}
                <div className="d-bn-icon">{n.icon}</div>
                <span className="d-bn-label">{n.label}</span>
              </div>
            ))}
          </div>
        </nav>

        {/* MOBILE COUNTRY SHEET */}
        {/* Country sheet disabled — country locked to profile */}
        <div style={{ display: "none" }}>
        <div className={`d-country-sheet`}>
          <div className="d-sheet-handle" />
          <div className="d-sheet-title">Select Your Country</div>
          {COUNTRIES.map((c) => (
            <div
              key={c.code}
              className={`d-country-option ${
                country === c.code ? "selected" : ""
              }`}
              onClick={() => handleCountry(c.code)}
            >
              <div>
                <div className="d-country-option-name">{c.flag} {c.name}</div>
                <div className="d-country-option-cities">
                  {c.cities.join(" · ")}
                </div>
              </div>
              {country === c.code && (
                <span style={{ color: "var(--teal)", fontSize: 16 }}>✓</span>
              )}
            </div>
          ))}
        </div>
        </div>{/* end hidden country sheet */}
      </div>
    </>
  );
}
